import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import Form from "../../Components/Form";
import Button from "../../Components/Button";
import { updateUser, uploadUserImage, getAdminDropdownOptions, getAccountContact, updateAccountContact } from "../../Utils/apiServices";
import { useAuth } from "../../Context/authContext";
import Avatar from "../../Components/Avatar";
import { CountrySelect, StateSelect, CitySelect, GetCountries, GetState } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import { getTimezoneOptionsForCountry, getCountryCodeFromName } from "../../Utils/constants";
import { stripNumbersFromName } from "../../Utils/nameValidation";
import PhoneVerify, { buildE164 } from "../../Components/PhoneVerify";
import GeminiAutocomplete from "../../Components/GeminiAutocomplete";
import {
  MAX_EXPERIENCE_SELECT,
  SPONSORSHIP_PROGRAM_OPTIONS,
  INVESTOR_TYPE_OPTIONS,
  STAGE_FOCUS_OPTIONS,
  DOMAIN_FOCUS_OPTIONS,
  MINIMUM_TRACTION_OPTIONS,
  MANDATORY_PITCH_MATERIAL_OPTIONS,
} from "./constants";
import {
  parseE164Phone,
  buildE164Phone,
  parseMultiSelectStr,
  toCommaSeparated,
  isPartnerLikeRole,
  toApiSponsorshipProgram,
  normalizeSponsorshipForForm,
} from "./utils";
import CompletionScreen from "./CompletionScreen";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import TermsConditionsModal from "./TermsConditionsModal";
import { getEducationInfoFields } from "./formFields/getEducationInfoFields";
import { getStudentExtraFormFields } from "./formFields/getStudentExtraFormFields";

/**
 * CompleteRegistration - Self-registration form for partner/investor/mentor/student.
 * When targetUser prop is provided (admin edit mode), renders the same form for editing
 * that user's account+contact data via getAccountContact API.
 * When accountContactFromList is passed (from list view click), uses that data so primary = contacts[0], secondary = contacts[1].
 */
const CompleteRegistration = ({ targetUser: targetUserProp, isAdminEdit, accountContactFromList }) => {
  const navigate = useNavigate();
  const { user: authUser, updateUser: updateAuthUser } = useAuth();
  const user = isAdminEdit && targetUserProp ? targetUserProp : authUser;
  const [photo, setPhoto] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  // Phone ownership verification (Twilio Verify SMS OTP). Tracking the verified
  // number rather than a bare boolean keeps the flag alive across PhoneVerify
  // remounts and invalidates it the moment the number is edited.
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState("");
  const [currentPhoneE164, setCurrentPhoneE164] = useState("");
  const phoneVerified = Boolean(currentPhoneE164) && currentPhoneE164 === verifiedPhoneE164;

  const handlePhoneVerifiedChange = (isVerified, e164) => {
    setCurrentPhoneE164(e164 || "");
    if (isVerified) setVerifiedPhoneE164(e164 || "");
  };

  // Grandfather clause: this form edits an existing profile and prefills the phone
  // that is already on file. Treating that stored number as verified means nobody
  // is locked out of saving a profile they can't otherwise change. The gate still
  // bites when the field is empty or the number is edited. Dropping this requires a
  // server-side phone_verified flag — the client cannot tell "verified long ago"
  // from "never verified".
  //
  // Held in a ref captured once at hydration (see the load effect). Reading it from
  // initialFormData would be a hole: that state is replaced on every successful
  // save, so an unverified number saved as a draft would come back "grandfathered".
  const grandfatheredPhonesRef = useRef(null);
  const storedPhoneE164 = (phoneKey) => grandfatheredPhonesRef.current?.[phoneKey] || "";
  const [privacyPolicyAcknowledged, setPrivacyPolicyAcknowledged] = useState(false);
  const [termsConditionsAcknowledged, setTermsConditionsAcknowledged] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [hasViewedPrivacyPolicy, setHasViewedPrivacyPolicy] = useState(false);
  const [showTermsConditions, setShowTermsConditions] = useState(false);
  const [hasViewedTermsConditions, setHasViewedTermsConditions] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [completionType, setCompletionType] = useState(null); // "approved" or "pending"
  const formRef = useRef(null);

  // Dropdown options from API for mentor experience fields
  const [experienceOptions, setExperienceOptions] = useState({
    industry_experience: [],
    functional_experience: [],
    technology_stack: [],
  });
  // Partner-specific dropdown options
  const [partnerOptions, setPartnerOptions] = useState({
    partner_type: [],
    partner_category: [],
    benefit_type: [],
    reporting_frequency: [],
  });
  // Investor-specific dropdown options
  const [investorOptions, setInvestorOptions] = useState({
    investor_type: [],
    stage_focus: [],
    domain_focus: [],
    minimum_traction: [],
    mandatory_pitch_material: [],
  });

  // Get profile data from localStorage (authUser)
  const getProfileData = useCallback(() => {
    try {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData;
      }
      const storedProfile = localStorage.getItem(`profile_${user?.id || user?.email}`);
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        return profile;
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
    }
    return null;
  }, [user?.id, user?.email]);

  // Load profile data on mount and when user changes (resolve country/state ids in same run to avoid effect loop)
  // For admin edit: use targetUser + getAccountContact only. For self: use authUser + localStorage + getAccountContact.
  useEffect(() => {
    if (!user) return;

    const runLoad = async () => {
      const profileDataFromStorage = isAdminEdit ? null : getProfileData();

      // Helper function to get value from storage (handles both formats) or user object
      const getValue = (formKey, storageSnakeKey, userKey, defaultValue = "") => {
        // Check storage with camelCase (form format)
        if (profileDataFromStorage?.[formKey] !== undefined && profileDataFromStorage?.[formKey] !== null && profileDataFromStorage?.[formKey] !== "") {
          return profileDataFromStorage[formKey];
        }
        // Check storage with snake_case (API format)
        if (profileDataFromStorage?.[storageSnakeKey] !== undefined && profileDataFromStorage?.[storageSnakeKey] !== null && profileDataFromStorage?.[storageSnakeKey] !== "") {
          return profileDataFromStorage[storageSnakeKey];
        }
        // Check user object
        if (user[userKey] !== undefined && user[userKey] !== null && user[userKey] !== "") {
          return user[userKey];
        }
        return defaultValue;
      };
      const rawPhone = getValue("phone", "phone", "phone", "");
      const parsedPhone = parseE164Phone(rawPhone);
      let profileData = {
        firstName: getValue("firstName", "first_name", "first_name", ""),
        lastName: getValue("lastName", "last_name", "last_name", ""),
        email: getValue("email", "email", "email", ""),
        phone: parsedPhone ? parsedPhone.nationalNumber : rawPhone,
        phone_country_code: parsedPhone ? parsedPhone.countryCode : (profileDataFromStorage?.phone_country_code || "US"),
        country: getValue("country", "country", "country", "United States"),
        state: getValue("state", "state", "state", ""),
        city: getValue("city", "city", "city", ""),
        timezone: getValue("timezone", "timezone", "timezone", ""),
        photo: user?.profile_picture_url || profileDataFromStorage?.photo || null,
        linkedin_profile: (() => {
          const linkedinUrl = profileDataFromStorage?.linkedin_profile || 
                             profileDataFromStorage?.linkedin_url || 
                             user?.linkedin_url || "";
          if (linkedinUrl) {
            return linkedinUrl.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, "").replace(/^\/+/, "");
          }
          return "";
        })(),
        x_profile: (() => {
          const xUrl = profileDataFromStorage?.x_profile || 
                       profileDataFromStorage?.x_url || 
                       user?.x_url || "";
          if (xUrl) {
            return xUrl.replace(/^https?:\/\/(www\.)?(x\.com|twitter\.com)\//i, "").replace(/^\/+/, "").replace(/^@/, "");
          }
          return "";
        })(),
        secondary_email: getValue("secondary_email", "secondary_email", "secondary_email", ""),
        tag_line: getValue("tag_line", "tagline", "tagline", ""),
        bio: getValue("bio", "bio", "bio", ""),
        // Student education fields
        collage: getValue("collage", "collage", "collage", ""),
        year: getValue("year", "year", "year", ""),
        primary_stream: getValue("primary_stream", "primary_stream", "primary_stream", ""),
        specialization: getValue("specialization", "specialization", "specialization", ""),
        // Student GitHub and reflection fields (strip github prefix for display in input)
        github_url: (() => {
          const v = getValue("github_url", "github_url", "github_url", "");
          if (v && (v.startsWith("https://github.com/") || v.startsWith("http://github.com/"))) {
            return v.replace(/^https?:\/\/github\.com\//i, "").replace(/^\/+/, "");
          }
          return v || "";
        })(),
        innovation_excites_you: getValue("innovation_excites_you", "innovation_excites_you", "innovation_excites_you", ""),
        success: getValue("success", "success", "success", ""),
        achievement: getValue("achievement", "achievement", "achievement", ""),
        years_of_experience: (() => {
          const value = profileDataFromStorage?.years_of_experience ?? 
                       profileDataFromStorage?.yearsOfExperience ??
                       user.years_of_experience ?? 
                       user.experience_years;
          return value !== null && value !== undefined ? String(value) : "";
        })(),
        industry_experience: parseMultiSelectStr(getValue("industry_experience", "industry", "industry", "")),
        functional_experience: parseMultiSelectStr(getValue("functional_experience", "functional_experience", "functional_experience", "")),
        technology_stack: parseMultiSelectStr(getValue("technology_stack", "technology_stack", "technology_stack", "")),
        maximum_engagements: (() => {
          const value = profileDataFromStorage?.maximum_engagements ?? 
                       profileDataFromStorage?.maximum_engagements_assigned ?? 
                       user.maximum_engagements_assigned;
          return value !== null && value !== undefined ? String(value) : "";
        })(),
        // Partner-specific fields (shared keys; role-specific forms use subset)
        partner_name: getValue("partner_name", "sponsor_name", "sponsor_name", ""),
        partner_type: getValue("partner_type", "legal_entity_name", "legal_entity_name", ""),
        partner_category: getValue("partner_category", "industry_sector", "industry_sector", ""),
        website_url: getValue("website_url", "website_url", "website_url", ""),
        funding_amount: (() => {
          const value = profileDataFromStorage?.funding_configuration ?? user?.funding_configuration;
          return value !== null && value !== undefined ? String(value) : "";
        })(),
        // Corporate sponsor–specific
        sponsor_name: getValue("sponsor_name", "sponsor_name", "sponsor_name", "") || getValue("partner_name", "sponsor_name", "sponsor_name", ""),
        legal_entity_name: getValue("legal_entity_name", "legal_entity_name", "legal_entity_name", "") || getValue("partner_type", "legal_entity_name", "legal_entity_name", ""),
        industry_sector: parseMultiSelectStr(getValue("industry_sector", "industry_sector", "industry_sector", "") || getValue("partner_category", "industry_sector", "industry_sector", "")),
        sponsorship_program: (() => {
          const v = getValue("sponsorship_program", "funding_configuration", "funding_configuration", "");
          if (v) return v;
          const amt = profileDataFromStorage?.funding_amount ?? user?.funding_amount;
          if (typeof amt === "number") {
            if (amt < 5000) return "bronze";
            if (amt < 15000) return "silver";
            if (amt < 25000) return "gold";
            return "platinum";
          }
          return "";
        })(),
        contact_name: getValue("contact_name", "contact_name", "contact_name", ""),
        contact_role: getValue("contact_role", "contact_role", "contact_role", ""),
        external_contact_name: getValue("external_contact_name", "contact_name", "contact_name", ""),
        external_contact_role: getValue("external_contact_role", "contact_role", "contact_role", ""),
        contact_email: getValue("contact_email", "contact_email", "contact_email", ""),
        contact_phone: (() => {
          const raw = getValue("contact_phone", "contact_phone", "contact_phone", "");
          const parsed = parseE164Phone(raw);
          return parsed ? parsed.nationalNumber : raw;
        })(),
        contact_phone_country_code: (() => {
          const raw = getValue("contact_phone", "contact_phone", "contact_phone", "");
          const parsed = parseE164Phone(raw);
          return parsed ? parsed.countryCode : (profileDataFromStorage?.contact_phone_country_code || "US");
        })(),
        secondary_contact_name: getValue("secondary_contact_name", "secondary_contact_name", "secondary_contact_name", ""),
        secondary_contact_role: getValue("secondary_contact_role", "secondary_contact_role", "secondary_contact_role", ""),
        secondary_contact_email: getValue("secondary_contact_email", "secondary_contact_email", "secondary_contact_email", ""),
        secondary_contact_phone: (() => {
          const raw = getValue("secondary_contact_phone", "secondary_contact_phone", "secondary_contact_phone", "");
          const parsed = parseE164Phone(raw);
          return parsed ? parsed.nationalNumber : raw;
        })(),
        secondary_contact_phone_country_code: (() => {
          const raw = getValue("secondary_contact_phone", "secondary_contact_phone", "secondary_contact_phone", "");
          const parsed = parseE164Phone(raw);
          return parsed ? parsed.countryCode : (profileDataFromStorage?.secondary_contact_phone_country_code || "US");
        })(),
        benefit_name: getValue("benefit_name", "benefit_name", "benefit_name", ""),
        benefit_type: getValue("benefit_type", "benefit_type", "benefit_type", ""),
        benefit_description: getValue("benefit_description", "benefit_description", "benefit_description", ""),
        validity_period: (() => {
          const v = getValue("validity_period", "validity_period", "validity_period", "");
          if (v) return v;
          const today = new Date();
          const oneYearLater = new Date(today);
          oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
          return oneYearLater.toISOString().split("T")[0];
        })(),
        benefit_renewable: profileDataFromStorage?.benefit_renewable ?? false,
        provisioning_process: getValue("provisioning_process", "provisioning_process", "provisioning_process", ""),
        mentorship_registration: profileDataFromStorage?.mentorship_registration ?? false,
        pilot_access: profileDataFromStorage?.pilot_access ?? false,
        sponsor_pitch_day: getValue("sponsor_pitch_day", "sponsor_pitch_day", "sponsor_pitch_day", ""),
        reporting_frequency: getValue("reporting_frequency", "reporting_frequency", "reporting_frequency", ""),
        public_visibility: profileDataFromStorage?.public_visibility ?? user?.public_visibility ?? true,
        // Investor-specific fields
        investor_name: getValue("investor_name", "sponsor_name", "sponsor_name", ""),
        investor_type: getValue("investor_type", "legal_entity_name", "legal_entity_name", ""),
        fund_size_range: getValue("fund_size_range", "funding_configuration", "funding_configuration", ""),
        stage_focus: getValue("stage_focus", "stage_focus", "stage_focus", ""),
        domain_focus: getValue("domain_focus", "domain_focus", "domain_focus", ""),
        investment_renewable: profileDataFromStorage?.investment_renewable ?? false,
        minimum_traction: getValue("minimum_traction", "minimum_traction", "minimum_traction", ""),
        mandatory_pitch_material: getValue("mandatory_pitch_material", "mandatory_pitch_material", "mandatory_pitch_material", ""),
      };

      // For corporate_sponsor, ecosystem_partner, investor: load account + contacts (primary = contacts[0], secondary = contacts[1])
      // When accountContactFromList is passed from list view, use it (same shape as list API). Otherwise fetch via getAccountContact(user.id).
      if ((isPartnerLikeRole(user?.role) || user?.role === "investor") && user?.id) {
        const buildMerged = (acc, contact, secondaryContact) => {
          const cp = parseE164Phone(contact?.phone);
          const contactName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() || undefined : undefined;
          const stripLinkedin = (v) => (v && String(v).replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, "").replace(/^\/+/, "")) || "";
          const stripX = (v) => (v && String(v).replace(/^https?:\/\/(www\.)?(x\.com|twitter\.com)\//i, "").replace(/^\/+/, "").replace(/^@/, "")) || "";
          const amt = acc?.funding_amount != null ? parseFloat(acc.funding_amount) : NaN;
          const sponsorshipFromAmount = !isNaN(amt)
            ? amt < 5000
              ? "bronze"
              : amt < 15000
                ? "silver"
                : amt < 25000
                  ? "gold"
                  : "platinum"
            : undefined;
          const secondaryName = secondaryContact
            ? [secondaryContact.first_name, secondaryContact.last_name].filter(Boolean).join(" ").trim() || ""
            : "";
          const secondaryPhoneParsed = secondaryContact?.phone ? parseE164Phone(secondaryContact.phone) : null;
          return {
            sponsor_name: acc?.name ?? profileData.sponsor_name,
            legal_entity_name: acc?.legal_entity_name ?? profileData.legal_entity_name,
            industry_sector: acc?.industry != null ? parseMultiSelectStr(String(acc.industry)) : profileData.industry_sector,
            partner_name: acc?.name ?? profileData.partner_name,
            partner_type: acc?.type ?? acc?.legal_entity_name ?? profileData.partner_type,
            partner_category: acc?.category ?? acc?.Category ?? acc?.industry ?? profileData.partner_category,
            investor_name: acc?.name ?? profileData.investor_name,
            investor_type: acc?.type ?? acc?.legal_entity_name ?? profileData.investor_type,
            website_url: acc?.website ?? profileData.website_url,
            funding_amount: acc?.funding_amount ?? profileData.funding_amount,
            fund_size_range: acc?.fund_range ?? acc?.funding_configuration ?? profileData.fund_size_range,
            sponsorship_program: normalizeSponsorshipForForm(sponsorshipFromAmount ?? (acc?.SponsorshipProgram ?? acc?.funding_configuration ?? profileData.sponsorship_program)),
            tag_line: acc?.tagline ?? contact?.tagline ?? profileData.tag_line,
            photo: acc?.company_logo ?? contact?.profile_picture_url ?? profileData.photo,
            linkedin_profile: stripLinkedin(acc?.linkedin ?? contact?.linkedin_url) || profileData.linkedin_profile,
            x_profile: stripX(acc?.x ?? contact?.x_url) || profileData.x_profile,
            reporting_frequency: acc?.report_frequency ?? profileData.reporting_frequency,
            sponsor_pitch_day: acc?.SponsorPitchDay ?? acc?.sponsor_pitch_day ?? profileData.sponsor_pitch_day,
            // Default public profile on when account has not set profile_visibility (contact may still carry false from API defaults)
            public_visibility: acc?.profile_visibility != null ? !!acc.profile_visibility : true,
            mentorship_registration: acc?.mentorship ?? profileData.mentorship_registration ?? false,
            pilot_access: acc?.pilot_access ?? profileData.pilot_access ?? false,
            benefit_name: acc?.benefit_name ?? profileData.benefit_name,
            benefit_type: acc?.benefit_type ?? profileData.benefit_type,
            benefit_description: acc?.benefit_description ?? profileData.benefit_description,
            validity_period: acc?.validity ?? profileData.validity_period,
            provisioning_process: acc?.ProvisioningProcess ?? acc?.provisioning_process ?? profileData.provisioning_process,
            stage_focus: acc?.stage_focus ?? profileData.stage_focus,
            domain_focus: acc?.domain_focus ?? profileData.domain_focus,
            minimum_traction: acc?.traction_required ?? profileData.minimum_traction,
            mandatory_pitch_material: acc?.pitch_material ?? profileData.mandatory_pitch_material,
            accountId: acc?.id,
            account_id: acc?.id,
            primary_contact_id: contact?.id ?? profileData.primary_contact_id,
            secondary_contact_id: secondaryContact?.id ?? profileData.secondary_contact_id,
            primary_contact_username: contact?.username ?? profileData.primary_contact_username,
            secondary_contact_username: secondaryContact?.username ?? profileData.secondary_contact_username,
            contact_name: contactName ?? profileData.contact_name,
            external_contact_name: contactName ?? profileData.external_contact_name,
            contact_email: contact?.email ?? profileData.contact_email,
            contact_phone: cp ? cp.nationalNumber : (contact?.phone ?? profileData.contact_phone),
            contact_phone_country_code: cp ? cp.countryCode : profileData.contact_phone_country_code,
            contact_role: contact?.company_role ?? profileData.contact_role,
            external_contact_role: contact?.company_role ?? profileData.external_contact_role ?? profileData.contact_role,
            firstName: contact?.first_name ?? profileData.firstName,
            lastName: contact?.last_name ?? profileData.lastName,
            email: contact?.email ?? profileData.email,
            phone: cp ? cp.nationalNumber : (contact?.phone ? parseE164Phone(contact.phone)?.nationalNumber : profileData.phone),
            phone_country_code: cp ? cp.countryCode : (contact?.phone ? parseE164Phone(contact.phone)?.countryCode : profileData.phone_country_code),
            country: contact?.country ?? profileData.country,
            state: contact?.state ?? profileData.state,
            city: contact?.city ?? profileData.city,
            timezone: contact?.timezone ?? profileData.timezone,
            bio: contact?.bio ?? profileData.bio,
            secondary_email: contact?.secondary_email ?? profileData.secondary_email,
            secondary_contact_name: secondaryName || profileData.secondary_contact_name,
            secondary_contact_role: secondaryContact?.company_role ?? profileData.secondary_contact_role,
            secondary_contact_email: secondaryContact?.email ?? profileData.secondary_contact_email,
            secondary_contact_phone: secondaryPhoneParsed ? secondaryPhoneParsed.nationalNumber : (secondaryContact?.phone ?? profileData.secondary_contact_phone),
            secondary_contact_phone_country_code: secondaryPhoneParsed ? secondaryPhoneParsed.countryCode : profileData.secondary_contact_phone_country_code,
          };
        };

        if (accountContactFromList?.account && Array.isArray(accountContactFromList.contacts)) {
          const acc = accountContactFromList.account;
          const contacts = accountContactFromList.contacts;
          const primary = contacts[0];
          const secondary = contacts[1] || null;
          profileData = { ...profileData, ...buildMerged(acc, primary, secondary) };
        } else {
          try {
            const res = await getAccountContact(user.id);
            const body = res?.data;
            if (!body?.success) return;
            const rawData = body?.data;
            if (Array.isArray(rawData) && rawData.length > 0) {
              const item = rawData[0];
              const acc = item?.account || {};
              const contacts = Array.isArray(item?.contacts) ? item.contacts : [];
              const primary = contacts[0] || null;
              const secondary = contacts[1] || null;
              profileData = { ...profileData, ...buildMerged(acc, primary, secondary) };
            } else if (rawData && typeof rawData === "object") {
              const acc = rawData?.account || {};
              const contact = rawData?.contact || {};
              profileData = { ...profileData, ...buildMerged(acc, contact, null) };
            }
          } catch (err) {
            console.error("Error loading account-contact:", err);
          }
        }
      }

      const applyProfile = async (data) => {
        const withResolvedIds = { ...data };
        if (data.country && data.countryCode == null) {
          const code = getCountryCodeFromName(data.country);
          if (code) withResolvedIds.countryCode = code;
        }
        if (data.country && data.countryid == null) {
          try {
            const countriesList = await GetCountries();
            const found = countriesList?.find((c) => c.name === data.country);
            if (found) withResolvedIds.countryid = found.id;
          } catch (_) {}
        }
        if (withResolvedIds.countryid != null && data.state && data.stateid == null) {
          try {
            const statesList = await GetState(withResolvedIds.countryid);
            const found = statesList?.find((s) => s.name === data.state);
            if (found) withResolvedIds.stateid = found.id;
          } catch (_) {}
        }
        setFormData(withResolvedIds);
        setInitialFormData(withResolvedIds);
        // Snapshot the number that was on file when this form first hydrated. Taken
        // once, and never from initialFormData, which is overwritten after every
        // save — otherwise saving a draft would launder an unverified number into a
        // grandfathered one and bypass the OTP completely.
        if (grandfatheredPhonesRef.current === null) {
          grandfatheredPhonesRef.current = {
            phone: buildE164(withResolvedIds.phone_country_code, withResolvedIds.phone),
            contact_phone: buildE164(
              withResolvedIds.contact_phone_country_code,
              withResolvedIds.contact_phone
            ),
          };
        }
        setPhoto(data.photo || null);
      };
      applyProfile(profileData);
    };
    runLoad();
  }, [user?.id, user?.role, user?.email, getProfileData, isAdminEdit, accountContactFromList?.account?.id]);

  // Fetch dropdown options for mentor experience fields (admin dropdown-options API)
  useEffect(() => {
    const parseOptions = (res) => {
      if (!res?.success || res?.data === undefined) return [];
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload) ? payload : (payload?.options ?? []);
      return (Array.isArray(list) ? list : [])
        .filter((o) => o && (o?.is_active !== false && o?.enabled !== false))
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((o) => ({ value: String(o?.value ?? o?.label ?? ""), label: String(o?.value ?? o?.label ?? "") }))
        .filter((o) => o.value);
    };
    const fetchExperienceDropdowns = async () => {
      try {
        const [industryRes, functionalRes, techRes] = await Promise.all([
          getAdminDropdownOptions("industry_experience", true),
          getAdminDropdownOptions("functional_experience", true),
          getAdminDropdownOptions("technology_stack", true),
        ]);
        setExperienceOptions({
          industry_experience: parseOptions(industryRes),
          functional_experience: parseOptions(functionalRes),
          technology_stack: parseOptions(techRes),
        });
      } catch (err) {
        console.error("Error loading experience dropdown options:", err);
      }
    };
    fetchExperienceDropdowns();
  }, []);

  // Fetch dropdown options for partner fields
  useEffect(() => {
    if (!isPartnerLikeRole(user?.role)) return;
    
    const parseOptions = (res) => {
      if (!res?.success || res?.data === undefined) return [];
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload) ? payload : (payload?.options ?? []);
      return (Array.isArray(list) ? list : [])
        .filter((o) => o && (o?.is_active !== false && o?.enabled !== false))
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((o) => ({ value: String(o?.value ?? o?.label ?? ""), label: String(o?.value ?? o?.label ?? "") }))
        .filter((o) => o.value);
    };
    
    const fetchPartnerDropdowns = async () => {
      try {
        // Fetch partner-specific dropdowns - using generic keys that might exist
        // You may need to adjust these keys based on your API
        const [partnerTypeRes, partnerCategoryRes, benefitTypeRes, reportingFreqRes] = await Promise.all([
          getAdminDropdownOptions("partner_type", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("partner_category", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("benefit_type", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("reporting_frequency", true).catch(() => ({ success: false, data: [] })),
        ]);
        
        setPartnerOptions({
          partner_type: parseOptions(partnerTypeRes),
          partner_category: parseOptions(partnerCategoryRes),
          benefit_type: parseOptions(benefitTypeRes),
          reporting_frequency: parseOptions(reportingFreqRes),
        });
      } catch (err) {
        console.error("Error loading partner dropdown options:", err);
      }
    };
    fetchPartnerDropdowns();
  }, [user?.role]);

  // Fetch dropdown options for investor fields
  useEffect(() => {
    if (user?.role !== "investor") return;
    
    const parseOptions = (res) => {
      if (!res?.success || res?.data === undefined) return [];
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload) ? payload : (payload?.options ?? []);
      return (Array.isArray(list) ? list : [])
        .filter((o) => o && (o?.is_active !== false && o?.enabled !== false))
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((o) => ({ value: String(o?.value ?? o?.label ?? ""), label: String(o?.value ?? o?.label ?? "") }))
        .filter((o) => o.value);
    };
    
    const fetchInvestorDropdowns = async () => {
      try {
        const [investorTypeRes, stageFocusRes, domainFocusRes, minTractionRes, pitchMaterialRes] = await Promise.all([
          getAdminDropdownOptions("investor_type", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("stage_focus", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("domain_focus", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("minimum_traction", true).catch(() => ({ success: false, data: [] })),
          getAdminDropdownOptions("mandatory_pitch_material", true).catch(() => ({ success: false, data: [] })),
        ]);
        
        setInvestorOptions({
          investor_type: parseOptions(investorTypeRes),
          stage_focus: parseOptions(stageFocusRes),
          domain_focus: parseOptions(domainFocusRes),
          minimum_traction: parseOptions(minTractionRes),
          mandatory_pitch_material: parseOptions(pitchMaterialRes),
        });
      } catch (err) {
        console.error("Error loading investor dropdown options:", err);
      }
    };
    fetchInvestorDropdowns();
  }, [user?.role]);

  const acknowledgementSectionHeader = (
    <div className="flex flex-col gap-2 py-4 mt-4 border-t border-b border-slate-200 text-left">
      <Typography as="h2" className="text-lg font-bold text-primary-900">
        Your Acknowledgement
      </Typography>
      <Typography as="p" className="text-sm text-tertiary-600">
        Please read the Privacy Policy and Terms and Conditions, then confirm your acknowledgement using the checkbox below.
      </Typography>
    </div>
  );

  const renderPrivacyAcknowledgementRow = (checkboxId, handleInputChange) => {
    const bothViewed = hasViewedPrivacyPolicy && hasViewedTermsConditions;
    return (
      <div className="w-full flex items-start gap-3 text-left py-4 border-b border-slate-200">
        <input
          type="checkbox"
          id={checkboxId}
          checked={privacyPolicyAcknowledged}
          onChange={(e) => {
            const checked = e.target.checked;
            setPrivacyPolicyAcknowledged(checked);
            setTermsConditionsAcknowledged(checked);
            handleInputChange("privacy_policy", checked);
            handleInputChange("terms_conditions", checked);
          }}
          disabled={!bothViewed}
          className={`mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-blue focus:ring-brand-blue ${
            !bothViewed ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />
        <label htmlFor={checkboxId} className="text-sm text-slate-700 cursor-pointer text-left flex-1 min-w-0 leading-relaxed">
          I hereby acknowledge the{" "}
          <button
            type="button"
            className="text-brand-blue hover:underline bg-transparent border-0 p-0 cursor-pointer font-inherit text-left"
            onClick={(e) => {
              e.preventDefault();
              setShowPrivacyPolicy(true);
            }}
          >
            Privacy Policy
          </button>{" "}
          and{" "}
          <button
            type="button"
            className="text-brand-blue hover:underline bg-transparent border-0 p-0 cursor-pointer font-inherit text-left"
            onClick={(e) => {
              e.preventDefault();
              setShowTermsConditions(true);
            }}
          >
            Terms and Conditions
          </button>{" "}
          given.
          <span className="text-xs text-slate-500 block mt-2">
            (Please read the Privacy Policy and Terms and Conditions first)
          </span>
        </label>
      </div>
    );
  };

  const getInvestorFormFields = () => {
    const fields = [];

    // Core Identity Section
    fields.push(
      {
        name: "section_core_identity",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-b border-slate-200 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Core Identity
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Update your logo and contact details here.
            </Typography>
          </div>
        ),
      },
      {
        name: "investor_name",
        label: "Investment Entity",
        type: "text",
        required: true,
        placeholder: "Enter the investment entity name here",
      },
      {
        name: "investor_type",
        label: "Investment Profile",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.investor_type || ""}
              onChange={(e) => handleInputChange("investor_type", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select your investment profile</option>
              {INVESTOR_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.hint ? `${opt.label} - ${opt.hint}` : opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "website_url",
        label: "Portfolio / Firm URL",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700 text-left" htmlFor="investor-website-url">
              Portfolio / Firm URL <span className="text-red-500">*</span>
            </label>
            <GeminiAutocomplete
              inputId="investor-website-url"
              label="Portfolio / Firm URL"
              value={formData.website_url || ""}
              onChange={(nextValue) => handleInputChange("website_url", nextValue)}
              placeholder="e.g. microsoft.com or https://www.google.com"
              helperText="Type for AI suggestions. You can enter a domain or a full https:// URL."
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        ),
      },
      {
        name: "photo",
        label: "Brand Identity",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-2">
            <Typography as="p" className="text-xs text-tertiary-600">
              Please upload a high-resolution logo (SVG preferred).
            </Typography>
            <Typography as="p" className="text-xs text-tertiary-600">
              This will be displayed on the website.
            </Typography>
            <div className="flex items-center gap-4">
              <Avatar
                src={photo}
                firstName={formData.investor_name || user?.first_name}
                lastName=""
                email={formData.email || user?.email}
                alt="Investor logo"
                size="w-20 h-20"
                showBorder={true}
                className="border-2 border-slate-200"
              />
              <label
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Icon icon="lucide:upload" className="h-8 w-8 text-purple-500 mb-2" />
                  <Typography as="p" className="text-sm text-slate-600 text-center px-4">
                    Click to upload or drag and drop
                  </Typography>
                  <Typography as="p" className="text-xs text-slate-500 mt-1">
                    SVG, PNG, JPG or GIF files. 1600x1000px
                  </Typography>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPhoto(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        ),
      },
      {
        name: "tag_line",
        label: "Mission Statement",
        type: "text",
        required: false,
        placeholder: "A brief statement that summarizes your purpose and how you invest.",
      }
    );

    // Socials Info Section
    fields.push(
      {
        name: "section_socials",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Socials Info
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Add your social network information here.
            </Typography>
          </div>
        ),
      },
      {
        name: "linkedin_profile",
        label: "LinkedIn Profile",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex items-center">
            <span className="px-4 py-2.5 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-600">
              https://linkedin.com/in/
            </span>
            <input
              type="text"
              value={formData.linkedin_profile || ""}
              onChange={(e) => handleInputChange("linkedin_profile", e.target.value)}
              placeholder="Enter the profile name here"
              className={`flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
            />
          </div>
        ),
      },
      {
        name: "x_profile",
        label: "X Profile",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex items-center">
            <span className="px-4 py-2.5 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-600">
              https://x.com/
            </span>
            <input
              type="text"
              value={formData.x_profile || ""}
              onChange={(e) => handleInputChange("x_profile", e.target.value)}
              placeholder="Enter the profile name here"
              className={`flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
            />
          </div>
        ),
      }
    );

    // Direct Engagement Points (Complete Registration spec)
    fields.push(
      {
        name: "section_contact",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Direct Engagement Points
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Ensure we have the right channels to coordinate and collaborate on initiatives.
            </Typography>
          </div>
        ),
      },
      {
        name: "contact_name",
        label: "Lead Partnership Representative",
        type: "text",
        required: true,
        placeholder: "Enter the contact name here",
      },
      {
        name: "contact_role",
        label: "Designation / Title",
        type: "text",
        required: true,
        placeholder: "Enter the contact role here",
      },
      {
        name: "contact_email",
        label: "Lead Email Address",
        type: "email",
        required: true,
        placeholder: "Enter the contact email address here",
      },
      {
        name: "contact_phone",
        label: "Direct Line",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            countryCode={formData.contact_phone_country_code}
            phone={formData.contact_phone}
            onPhoneChange={(countryCode, phone) =>
              handleInputChange({ contact_phone_country_code: countryCode, contact_phone: phone })
            }
            onVerifiedChange={handlePhoneVerifiedChange}
            showVerify={!isAdminEdit}
            verifiedE164={storedPhoneE164("contact_phone")}
            placeholder="Enter the contact phone number here"
            required
            error={error}
          />
        ),
      },
      {
        name: "secondary_contact_name",
        label: "Secondary Partnership Representative",
        type: "text",
        required: false,
        placeholder: "Enter the contact name here",
      },
      {
        name: "secondary_contact_role",
        label: "Designation / Title",
        type: "text",
        required: false,
        placeholder: "Enter the contact role here",
      },
      {
        name: "secondary_contact_email",
        label: "Secondary Email Address",
        type: "email",
        required: false,
        placeholder: "Enter the contact email address here",
      },
      {
        name: "secondary_contact_phone",
        label: "Secondary Contact Number",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            showVerify={false}
            countryCode={formData.secondary_contact_phone_country_code}
            phone={formData.secondary_contact_phone}
            onPhoneChange={(countryCode, phone) =>
              handleInputChange({
                secondary_contact_phone_country_code: countryCode,
                secondary_contact_phone: phone,
              })
            }
            placeholder="Enter the contact phone number here"
            error={error}
          />
        ),
      }
    );

    // Investment Focus Section
    fields.push(
      {
        name: "section_investment_focus",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Investment Focus
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Let's understand your focus of investment.
            </Typography>
          </div>
        ),
      },
      {
        name: "stage_focus",
        label: "Stage Focus",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.stage_focus || ""}
              onChange={(e) => handleInputChange("stage_focus", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select the investment stage of focus here</option>
              {STAGE_FOCUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "domain_focus",
        label: "Domain Focus",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.domain_focus || ""}
              onChange={(e) => handleInputChange("domain_focus", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select the domain of focus here</option>
              {DOMAIN_FOCUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "investment_renewable",
        label: "Renewable",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-700 text-left">Renewable</span>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="investment_renewable"
                checked={formData.investment_renewable || false}
                onChange={(e) => handleInputChange("investment_renewable", e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
              />
              <label htmlFor="investment_renewable" className="text-sm text-slate-700 cursor-pointer">
                Make this benefit renewable.
              </label>
            </div>
          </div>
        ),
      }
    );

    // Screening Rules Section
    fields.push(
      {
        name: "section_screening",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Screening Rules
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Let's configure your screening rules here.
            </Typography>
          </div>
        ),
      },
      {
        name: "minimum_traction",
        label: "Minimum Traction Required",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.minimum_traction || ""}
              onChange={(e) => handleInputChange("minimum_traction", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select the minimum traction required</option>
              {MINIMUM_TRACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "mandatory_pitch_material",
        label: "Mandatory Pitch Material",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.mandatory_pitch_material || ""}
              onChange={(e) => handleInputChange("mandatory_pitch_material", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select the mandatory pitch material</option>
              {MANDATORY_PITCH_MATERIAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      }
    );

    // Acknowledgement Section
    fields.push(
      {
        name: "section_acknowledgment",
        type: "custom",
        component: acknowledgementSectionHeader,
      },
      {
        name: "privacy_policy",
        label: "",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange) =>
          renderPrivacyAcknowledgementRow("privacy_policy", handleInputChange),
      }
    );

    return fields;
  };

  const getCorporatePartnerFormFields = () => {
    const fields = [];
    fields.push(
      {
        name: "section_core_identity",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-b border-slate-200">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Core Identity
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Update your logo and core details here.
            </Typography>
          </div>
        ),
      },
      { name: "sponsor_name", label: "Company Name", type: "text", required: true, placeholder: "Enter the company name here" },
      { name: "legal_entity_name", label: "Registered Corporate Entity", type: "text", required: true, placeholder: "Enter the registered corporate entity name here" },
      {
        name: "industry_sector",
        label: "Primary Innovation Domain",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "Select at least one primary innovation domain" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.industry_sector) ? formData.industry_sector : (formData.industry_sector ? String(formData.industry_sector).split(",").map((s) => s.trim()).filter(Boolean) : []);
          const options = experienceOptions.industry_experience || [];
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("industry_sector", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${
                    error ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd (Windows) or Cmd (Mac) to select up to {MAX_EXPERIENCE_SELECT} domains ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                      {options.find((o) => o.value === item)?.label ?? item}
                      <button
                        type="button"
                        onClick={() => handleInputChange("industry_sector", arr.filter((x) => x !== item))}
                        className="hover:text-blue-900"
                        aria-label="Remove"
                      >
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {error && <span className="text-xs text-red-500">{error}</span>}
            </div>
          );
        },
      },
      {
        name: "website_url",
        label: "Corporate Portal",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700 text-left" htmlFor="corporate-partner-website-url">
              Corporate Portal <span className="text-red-500">*</span>
            </label>
            <GeminiAutocomplete
              inputId="corporate-partner-website-url"
              label="Corporate Portal"
              value={formData.website_url || ""}
              onChange={(nextValue) => handleInputChange("website_url", nextValue)}
              placeholder="e.g. portal.example.com or https://www.example.com"
              helperText="Type for AI suggestions. You can enter a domain or a full https:// URL."
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        ),
      },
      {
        name: "photo",
        label: "Brand Identity",
        type: "custom",
        required: true,
        helperText: "Please upload a high-resolution logo (SVG preferred).",
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                {photo ? (
                  <img src={photo} alt="Logo" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-slate-400">
                    <Icon icon="lucide:user" className="h-8 w-8" />
                  </div>
                )}
              </div>
              <label className="flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-600 text-sm cursor-pointer hover:bg-slate-100">
                <Icon icon="lucide:cloud-upload" className="h-6 w-6" />
                <span>Click to upload or drag and drop SVG, PNG, JPG or GIF (max 1920x1080px)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => setPhoto(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        ),
      },
      { name: "tag_line", label: "Mission Statement", type: "text", required: false, placeholder: "A concise statement that captures your organization’s purpose and impact." }
    );

    fields.push(
      {
        name: "section_contact",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">Direct Engagement Points</Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Ensure we have the right channels to coordinate and collaborate on initiatives.
            </Typography>
          </div>
        ),
      },
      { name: "contact_name", label: "Lead Partnership Representative", type: "text", required: true, placeholder: "Enter the contact name here" },
      { name: "contact_role", label: "Designation / Title", type: "text", required: true, placeholder: "Enter the contact role here" },
      { name: "contact_email", label: "Lead Email Address", type: "email", required: true, placeholder: "Enter the contact email address here" },
      {
        name: "contact_phone",
        label: "Direct Line",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            countryCode={formData.contact_phone_country_code}
            phone={formData.contact_phone}
            onPhoneChange={(countryCode, phone) => handleInputChange({ contact_phone_country_code: countryCode, contact_phone: phone })}
            onVerifiedChange={handlePhoneVerifiedChange}
            showVerify={!isAdminEdit}
            verifiedE164={storedPhoneE164("contact_phone")}
            placeholder="Enter the contact phone number here"
            required
            error={error}
          />
        ),
      },
      { name: "secondary_contact_name", label: "Secondary Partnership Representative", type: "text", required: false, placeholder: "Enter the contact name here" },
      { name: "secondary_contact_role", label: "Designation / Title", type: "text", required: false, placeholder: "Enter the contact role here" },
      { name: "secondary_contact_email", label: "Secondary Email Address", type: "email", required: false, placeholder: "Enter the contact email address here" },
      {
        name: "secondary_contact_phone",
        label: "Secondary Contact Number",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            showVerify={false}
            countryCode={formData.secondary_contact_phone_country_code}
            phone={formData.secondary_contact_phone}
            onPhoneChange={(countryCode, phone) => handleInputChange({ secondary_contact_phone_country_code: countryCode, secondary_contact_phone: phone })}
            placeholder="Enter the contact phone number here"
            error={error}
          />
        ),
      }
    );

    fields.push(
      {
        name: "section_funding",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">Partnership Tier Selection</Typography>
            <Typography as="p" className="text-sm text-tertiary-600">Tiers determine the level of brand visibility, VIP access, and recognition throughout the program journey.</Typography>
          </div>
        ),
      },
      {
        name: "sponsorship_program",
        label: "Partnership Tier Selection",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-4">
              {SPONSORSHIP_PROGRAM_OPTIONS.map((opt) => (
                <label key={opt.value} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sponsorship_program"
                    checked={(formData.sponsorship_program || "") === opt.value}
                    onChange={() => handleInputChange("sponsorship_program", opt.value)}
                    className="h-4 w-4 border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <span className="text-sm text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
            {error && <span className="text-xs text-red-500">Please select a partnership tier.</span>}
          </div>
        ),
      }
    );

    fields.push(
      {
        name: "section_support_corporate",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Governance & Support
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Define your support preferences
            </Typography>
          </div>
        ),
      },
      {
        name: "mentorship_registration",
        label: "Mentorship",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => (
          <div className="flex items-start gap-2 text-left">
            <input
              type="checkbox"
              id="mentorship_registration_corporate"
              checked={formData.mentorship_registration || false}
              onChange={(e) => handleInputChange("mentorship_registration", e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
            />
            <label htmlFor="mentorship_registration_corporate" className="text-sm text-slate-700 cursor-pointer">
              I would like to register as a mentor to be a part of the innovator program.
            </label>
          </div>
        ),
      },
      {
        name: "pilot_access",
        label: "Pilot Access",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => (
          <div className="flex items-start gap-2 text-left">
            <input
              type="checkbox"
              id="pilot_access_corporate"
              checked={formData.pilot_access || false}
              onChange={(e) => handleInputChange("pilot_access", e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
            />
            <label htmlFor="pilot_access_corporate" className="text-sm text-slate-700 cursor-pointer">
              I would like to register for pilot access.
            </label>
          </div>
        ),
      },
      {
        name: "sponsor_pitch_day",
        label: "Would you like to sponsor or host a Pitch Day?",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex gap-4">
            {["Yes", "Maybe", "No"].map((option) => (
              <div key={option} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={`sponsor_pitch_day_corporate_${option}`}
                  name="sponsor_pitch_day"
                  value={option}
                  checked={formData.sponsor_pitch_day === option}
                  onChange={(e) => handleInputChange("sponsor_pitch_day", e.target.value)}
                  className="h-4 w-4 text-brand-blue focus:ring-brand-blue"
                />
                <label htmlFor={`sponsor_pitch_day_corporate_${option}`} className="text-sm text-slate-700 cursor-pointer">
                  {option}
                </label>
              </div>
            ))}
          </div>
        ),
      },
      {
        name: "reporting_frequency",
        label: "Impact Reporting Cadence",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.reporting_frequency || ""}
              onChange={(e) => handleInputChange("reporting_frequency", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select your reporting frequency</option>
              {(partnerOptions.reporting_frequency || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      }
    );

    fields.push(
      {
        name: "section_visibility_corporate",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Ecosystem Presence
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Enable this to showcase your brand to the global IgniteNext community.
            </Typography>
          </div>
        ),
      },
      {
        name: "public_visibility",
        label: "",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => {
          const publicOn = formData.public_visibility ?? true;
          return (
          <div className="flex w-full items-center justify-start gap-3 text-left">
            <button
              type="button"
              role="switch"
              aria-checked={publicOn}
              aria-label="Public profile activation"
              onClick={() => handleInputChange("public_visibility", !publicOn)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 ${publicOn ? "bg-brand-blue" : "bg-slate-200"}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${publicOn ? "translate-x-5" : "translate-x-1"}`} />
            </button>
            <span className="text-sm text-slate-700 text-left">I would like to make my profile public.</span>
          </div>
          );
        },
      }
    );

    fields.push(
      {
        name: "section_acknowledgment_corporate",
        type: "custom",
        component: acknowledgementSectionHeader,
      },
      {
        name: "privacy_policy",
        label: "",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange) =>
          renderPrivacyAcknowledgementRow("privacy_policy_corporate", handleInputChange),
      }
    );
    return fields;
  };

  const getEcosystemPartnerFormFields = () => {
    const fields = [];

    // Core Identity & Presence (Complete Registration spec)
    fields.push(
      {
        name: "section_core_identity",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-b border-slate-200">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Core Identity & Presence
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Define how your organization will be represented within our global network.
            </Typography>
          </div>
        ),
      },
      {
        name: "partner_name",
        label: "Organization Name",
        type: "text",
        required: true,
        placeholder: "Enter the organization name here",
      },
      {
        name: "partner_type",
        label: "Partnership Model",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.partner_type || ""}
              onChange={(e) => handleInputChange("partner_type", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select partnership model</option>
              {(partnerOptions.partner_type || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "partner_category",
        label: "Ecosystem Domain",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              name="partner_category"
              value={formData.partner_category || ""}
              onChange={(e) => handleInputChange("partner_category", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select ecosystem domain</option>
              {(partnerOptions.partner_category || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "website_url",
        label: "Corporate Portal",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700 text-left" htmlFor="ecosystem-partner-website-url">
              Corporate Portal <span className="text-red-500">*</span>
            </label>
            <GeminiAutocomplete
              inputId="ecosystem-partner-website-url"
              label="Corporate Portal"
              value={formData.website_url || ""}
              onChange={(nextValue) => handleInputChange("website_url", nextValue)}
              placeholder="e.g. portal.example.com or https://www.example.com"
              helperText="Type for AI suggestions. You can enter a domain or a full https:// URL."
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        ),
      },
      {
        name: "photo",
        label: "Brand Identity",
        type: "custom",
        required: true,
        helperText: "Please upload a high-resolution logo (SVG preferred).",
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <Avatar
                src={photo}
                firstName={formData.partner_name || user?.first_name}
                lastName=""
                email={formData.email || user?.email}
                alt="Organization logo"
                size="w-20 h-20"
                showBorder={true}
                className="border-2 border-slate-200"
              />
              <label
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Icon icon="lucide:upload" className="h-8 w-8 text-purple-500 mb-2" />
                  <Typography as="p" className="text-sm text-slate-600 text-center px-4">
                    Click to upload or drag and drop
                  </Typography>
                  <Typography as="p" className="text-xs text-slate-500 mt-1">
                    SVG, PNG, JPG or GIF (max. 100x100px)
                  </Typography>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPhoto(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        ),
      },
      {
        name: "tag_line",
        label: "Mission Statement",
        type: "text",
        required: false,
        placeholder: "Share your organization's mission in one line",
      }
    );

    // Direct Engagement Points (Complete Registration spec)
    fields.push(
      {
        name: "section_contact",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Direct Engagement Points
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Ensure we have the right channels to coordinate and collaborate on initiatives.
            </Typography>
          </div>
        ),
      },
      {
        name: "external_contact_name",
        label: "Lead Partnership Representative",
        type: "text",
        required: true,
        placeholder: "Enter the contact name here",
      },
      {
        name: "external_contact_role",
        label: "Designation / Title",
        type: "text",
        required: true,
        placeholder: "Enter the contact role here",
      },
      {
        name: "contact_email",
        label: "Lead Email Address",
        type: "email",
        required: true,
        placeholder: "Enter the contact email address here",
      },
      {
        name: "contact_phone",
        label: "Direct Line",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            countryCode={formData.contact_phone_country_code}
            phone={formData.contact_phone}
            onPhoneChange={(countryCode, phone) =>
              handleInputChange({ contact_phone_country_code: countryCode, contact_phone: phone })
            }
            onVerifiedChange={handlePhoneVerifiedChange}
            showVerify={!isAdminEdit}
            verifiedE164={storedPhoneE164("contact_phone")}
            placeholder="Enter the contact phone number here"
            required
            error={error}
          />
        ),
      },
      {
        name: "secondary_contact_name",
        label: "Secondary Partnership Representative",
        type: "text",
        required: false,
        placeholder: "Enter the contact name here",
      },
      {
        name: "secondary_contact_role",
        label: "Designation / Title",
        type: "text",
        required: false,
        placeholder: "Enter the contact role here",
      },
      {
        name: "secondary_contact_email",
        label: "Secondary Email Address",
        type: "email",
        required: false,
        placeholder: "Enter the contact email address here",
      },
      {
        name: "secondary_contact_phone",
        label: "Secondary Contact Number",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            showVerify={false}
            countryCode={formData.secondary_contact_phone_country_code}
            phone={formData.secondary_contact_phone}
            onPhoneChange={(countryCode, phone) =>
              handleInputChange({
                secondary_contact_phone_country_code: countryCode,
                secondary_contact_phone: phone,
              })
            }
            placeholder="Enter the contact phone number here"
            error={error}
          />
        ),
      }
    );

    // Strategic Benefit Alignment (Complete Registration spec)
    fields.push(
      {
        name: "section_benefit",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Strategic Benefit Alignment
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Configure the specific resources and advantages your organization provides to the ecosystem.
            </Typography>
          </div>
        ),
      },
      {
        name: "benefit_name",
        label: "Resource Title",
        type: "text",
        required: true,
        placeholder: "e.g., Cloud Infrastructure Credits",
        helperText: "e.g., Cloud Infrastructure Credits, Executive Mentorship, or Lab Access.",
      },
      {
        name: "benefit_type",
        label: "Resource Classification",
        type: "custom",
        required: true,
        helperText: "Choose the category that best describes this contribution.",
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.benefit_type || ""}
              onChange={(e) => handleInputChange("benefit_type", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select a category</option>
              {(partnerOptions.benefit_type || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      },
      {
        name: "benefit_description",
        label: "Impact Details",
        type: "textarea",
        required: true,
        placeholder: "Describe how this resource supports the community",
        helperText: "How this benefit will empower the IgniteNext community.",
        rows: 4,
      },
      {
        name: "validity_period",
        label: "Active Impact Window",
        type: "custom",
        required: true,
        helperText: "The timeframe during which this resource will be accessible.",
        render: (value, formData, handleInputChange, error) => {
          const today = new Date().toISOString().split("T")[0];
          const endDate = formData.validity_period || "";
          return (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">Activation Date</label>
                <input
                  type="date"
                  value={today}
                  readOnly
                  disabled
                  className="w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-600 cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Expiration Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={endDate}
                  min={today}
                  onChange={(e) => handleInputChange("validity_period", e.target.value)}
                  placeholder="dd/mm/yyyy"
                  className={`w-full rounded-lg border ${
                    error ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
                />
                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
              </div>
            </div>
          );
        },
      },
      {
        name: "benefit_renewable",
        label: "",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="benefit_renewable"
              checked={formData.benefit_renewable || false}
              onChange={(e) => handleInputChange("benefit_renewable", e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
            />
            <label htmlFor="benefit_renewable" className="text-sm text-slate-700 cursor-pointer">
              Enable renewal for this resource
            </label>
          </div>
        ),
      }
    );

    // Provisioning & Fulfillment (Complete Registration spec)
    fields.push(
      {
        name: "section_provisioning",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Provisioning & Fulfillment
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Set your preferences for how services are delivered to our community.
            </Typography>
          </div>
        ),
      },
      {
        name: "provisioning_process",
        label: "Delivery & Fulfillment Protocol",
        type: "textarea",
        required: true,
        placeholder: "Describe the workflow for activation...",
        rows: 4,
      }
    );

    // Governance & Support (Complete Registration spec)
    fields.push(
      {
        name: "section_support",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Governance & Support
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Define your support preferences
            </Typography>
          </div>
        ),
      },
      {
        name: "mentorship_registration",
        label: "Mentorship",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => (
          <div className="flex items-start gap-2 text-left">
            <input
              type="checkbox"
              id="mentorship_registration"
              checked={formData.mentorship_registration || false}
              onChange={(e) => handleInputChange("mentorship_registration", e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
            />
            <label htmlFor="mentorship_registration" className="text-sm text-slate-700 cursor-pointer">
              I would like to register as a mentor to be a part of the innovator program.
            </label>
          </div>
        ),
      },
      {
        name: "pilot_access",
        label: "Pilot Access",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => (
          <div className="flex items-start gap-2 text-left">
            <input
              type="checkbox"
              id="pilot_access"
              checked={formData.pilot_access || false}
              onChange={(e) => handleInputChange("pilot_access", e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
            />
            <label htmlFor="pilot_access" className="text-sm text-slate-700 cursor-pointer">
              I would like to register for pilot access.
            </label>
          </div>
        ),
      },
      {
        name: "sponsor_pitch_day",
        label: "Would you like to sponsor or host a Pitch Day?",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex gap-4">
            {["Yes", "Maybe", "No"].map((option) => (
              <div key={option} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={`sponsor_pitch_day_${option}`}
                  name="sponsor_pitch_day"
                  value={option}
                  checked={formData.sponsor_pitch_day === option}
                  onChange={(e) => handleInputChange("sponsor_pitch_day", e.target.value)}
                  className="h-4 w-4 text-brand-blue focus:ring-brand-blue"
                />
                <label htmlFor={`sponsor_pitch_day_${option}`} className="text-sm text-slate-700 cursor-pointer">
                  {option}
                </label>
              </div>
            ))}
          </div>
        ),
      },
      {
        name: "reporting_frequency",
        label: "Impact Reporting Cadence",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.reporting_frequency || ""}
              onChange={(e) => handleInputChange("reporting_frequency", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select your reporting frequency</option>
              {(partnerOptions.reporting_frequency || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      }
    );

    // Ecosystem presence (public profile) — Complete Registration spec
    fields.push(
      {
        name: "section_visibility",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Ecosystem Presence
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Enable this to showcase your brand to the global IgniteNext community.
            </Typography>
          </div>
        ),
      },
      {
        name: "public_visibility",
        label: "",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange) => {
          const publicOn = formData.public_visibility ?? true;
          return (
          <div className="flex w-full items-center justify-start gap-3 text-left">
            <button
              type="button"
              role="switch"
              aria-checked={publicOn}
              aria-label="Public profile activation"
              onClick={() => handleInputChange("public_visibility", !publicOn)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 ${publicOn ? "bg-brand-blue" : "bg-slate-200"}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${publicOn ? "translate-x-5" : "translate-x-1"}`} />
            </button>
            <span className="text-sm text-slate-700 text-left">I would like to make my profile public.</span>
          </div>
          );
        },
      }
    );

    // Acknowledgement Section
    fields.push(
      {
        name: "section_acknowledgment",
        type: "custom",
        component: acknowledgementSectionHeader,
      },
      {
        name: "privacy_policy",
        label: "",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange) =>
          renderPrivacyAcknowledgementRow("privacy_policy_partner", handleInputChange),
      }
    );

    return fields;
  };

  const getFormFields = () => {
    let fields = [];
    // Return investor-specific fields if user is an investor
    if (user?.role === "investor") {
      fields = getInvestorFormFields();
    } else if (user?.role === "corporate_sponsor") {
      fields = getCorporatePartnerFormFields();
    } else if (user?.role === "ecosystem_partner") {
      fields = getEcosystemPartnerFormFields();
    } else if (user?.role === "student") {
      // Return student-specific fields (personal + education + social + github + reflection + privacy; no mentor experience section)
      const base = getMentorFormFieldsInternal().filter(
        (f) =>
          ![
            "section_experience",
            "years_of_experience",
            "industry_experience",
            "functional_experience",
            "technology_stack",
            "maximum_engagements",
          ].includes(f.name)
      );
      const socialIndex = base.findIndex((f) => f.name === "section_social");
      const education = getEducationInfoFields();
      let step1 = base;
      if (socialIndex >= 0) {
        step1 = [...base.slice(0, socialIndex), ...education, ...base.slice(socialIndex)];
      } else {
        step1 = [...education, ...base];
      }
      const ackIndex = step1.findIndex((f) => f.name === "section_acknowledgment");
      const extra = getStudentExtraFormFields();
      if (ackIndex >= 0) {
        return [...step1.slice(0, ackIndex), ...extra, ...step1.slice(ackIndex)];
      }
      return [...step1, ...extra];
    } else {
      return getMentorFormFieldsInternal();
    }
    // For partner/investor: in admin edit mode, hide privacy/acknowledgment section
    if (isAdminEdit && fields.length) {
      fields = fields.filter(
        (f) =>
          f.name !== "section_acknowledgment" &&
          f.name !== "section_acknowledgment_corporate" &&
          f.name !== "privacy_policy"
      );
    }
    return fields;
  };

  const getMentorFormFieldsInternal = () => {
    const fields = [];

    // Personal Info Section
    fields.push(
      {
        name: "section_personal",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-b border-slate-200">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Establish Your Global Footprint
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Showcase your identity to a borderless community of innovators
            </Typography>
          </div>
        ),
      },
      {
        name: "firstName",
        label: "Name",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error, errors = {}, touched = {}, handleBlur) => {
          const firstNameError = errors?.firstName && touched?.firstName;
          const lastNameError = errors?.lastName && touched?.lastName;
          const hasError = firstNameError || lastNameError;
          return (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.firstName || ""}
                  onChange={(e) => handleInputChange("firstName", stripNumbersFromName(e.target.value))}
                  onBlur={() => {
                    if (handleBlur) handleBlur("firstName");
                  }}
                  name="firstName"
                  placeholder="First Name"
                  className={`flex-1 rounded-lg border ${
                    firstNameError ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
                />
                <input
                  type="text"
                  value={formData.lastName || ""}
                  onChange={(e) => handleInputChange("lastName", stripNumbersFromName(e.target.value))}
                  onBlur={() => {
                    if (handleBlur) handleBlur("lastName");
                  }}
                  name="lastName"
                  placeholder="Last Name"
                  className={`flex-1 rounded-lg border ${
                    lastNameError ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
                />
              </div>
              {hasError && (
                <p className="text-xs text-red-500">
                  {firstNameError || lastNameError}
                </p>
              )}
            </div>
          );
        },
      },
      {
        name: "lastName",
        label: "",
        type: "hidden",
        required: true,
      },
      {
        name: "email",
        label: "Email address",
        type: "email",
        required: true,
        startIcon: "lucide:mail",
      },
      {
        name: "phone_country_code",
        label: "Phone",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <PhoneVerify
            countryCode={formData.phone_country_code}
            phone={formData.phone}
            onPhoneChange={(countryCode, phone) =>
              handleInputChange({ phone_country_code: countryCode, phone })
            }
            onVerifiedChange={handlePhoneVerifiedChange}
            showVerify={!isAdminEdit}
            verifiedE164={storedPhoneE164("phone")}
            placeholder="123-456-7890"
            required
            error={error}
          />
        ),
      },
      {
        name: "photo",
        label: "Your photo",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 text-left">
                Your photo
                <span className="text-red-500">*</span>
              </label>
              <Icon
                icon="lucide:info"
                className="h-4 w-4 text-slate-400"
              />
            </div>
            <Typography as="p" className="text-xs text-tertiary-600">
              This will be displayed on your profile.
            </Typography>
            <div className="flex items-center gap-4">
              <Avatar
                src={photo}
                firstName={formData.firstName || user?.first_name}
                lastName={formData.lastName || user?.last_name}
                email={formData.email || user?.email}
                alt="Profile photo"
                size="w-20 h-20"
                showBorder={true}
                className="border-2 border-slate-200"
              />
              <label
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Icon icon="lucide:upload" className="h-8 w-8 text-purple-500 mb-2" />
                  <Typography as="p" className="text-sm text-slate-600 text-center px-4">
                    Click to upload or drag and drop
                  </Typography>
                  <Typography as="p" className="text-xs text-slate-500 mt-1">
                    SVG, PNG, JPG or GIF (max. 1600x1600px)
                  </Typography>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPhoto(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        ),
      },
      {
        name: "country",
        label: "Country",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <CountrySelect
            containerClassName="w-full"
            inputClassName={`w-full rounded-lg border ${
              error ? "border-red-400" : "border-slate-200"
            } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
            placeHolder="Select Country"
            defaultValue={formData.country || ""}
            onChange={(e) => {
              handleInputChange("country", e?.name ?? "");
              handleInputChange("countryid", e?.id ?? null);
              handleInputChange("countryCode", e?.iso2 ?? "");
              handleInputChange("state", "");
              handleInputChange("stateid", null);
              handleInputChange("city", "");
              handleInputChange("timezone", "");
            }}
            showFlag={true}
          />
        ),
      },
      {
        name: "state",
        label: "State / City",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex gap-2">
            <div className="flex-1">
              <StateSelect
                containerClassName="w-full"
                inputClassName={`w-full rounded-lg border ${
                  error ? "border-red-400" : "border-slate-200"
                } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed`}
                placeHolder="Select State"
                countryid={formData.countryid ?? 0}
                defaultValue={formData.state || ""}
                onChange={(e) => {
                  handleInputChange("state", e?.name ?? "");
                  handleInputChange("stateid", e?.id ?? null);
                  handleInputChange("city", "");
                }}
              />
            </div>
            <div className="flex-1">
              <CitySelect
                containerClassName="w-full"
                inputClassName={`w-full rounded-lg border ${
                  error ? "border-red-400" : "border-slate-200"
                } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed`}
                placeHolder="Select City"
                countryid={formData.countryid ?? 0}
                stateid={formData.stateid ?? 0}
                defaultValue={formData.city || ""}
                onChange={(e) => handleInputChange("city", e?.name ?? "")}
              />
            </div>
          </div>
        ),
      },
      {
        name: "timezone",
        label: "Timezone",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => {
          const timezoneOptions = getTimezoneOptionsForCountry(formData.countryCode);
          return (
            <div className="relative">
              <Icon
                icon="lucide:clock"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10"
              />
              <select
                value={formData.timezone || ""}
                onChange={(e) => handleInputChange("timezone", e.target.value)}
                disabled={!formData.country}
                className={`w-full rounded-lg border ${
                  error ? "border-red-400" : "border-slate-200"
                } bg-white pl-10 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none disabled:bg-slate-100 disabled:cursor-not-allowed`}
              >
                <option value="">
                  {formData.country ? "Select timezone" : "Select a country first"}
                </option>
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <Icon
                icon="lucide:chevron-down"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
              />
            </div>
          );
        },
      }
    );

    // Social Info Section
    fields.push(
      {
        name: "section_social",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Extend Your Influence
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Connect your social presence to build trust and authority within the ecosystem
            </Typography>
          </div>
        ),
      },
      {
        name: "linkedin_profile",
        label: "LinkedIn Profile",
        type: "custom",
        required: false,
        render: (value, formData, handleInputChange, error) => (
          <div className="flex items-center">
            <span className="px-4 py-2.5 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-600">
              https://linkedin.com/in/
            </span>
            <input
              type="text"
              value={formData.linkedin_profile || ""}
              onChange={(e) => handleInputChange("linkedin_profile", e.target.value)}
              placeholder="Enter your LinkedIn profile here"
              className={`flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
            />
          </div>
        ),
      },
      {
        name: "secondary_email",
        label: "Secondary Email address",
        type: "email",
        required: true,
        startIcon: "lucide:mail",
      },
      {
        name: "tag_line",
        label: "Tag Line",
        type: "text",
        required: false,
        placeholder: "A brief one-liner that will summarize who you are.",
      },
      {
        name: "bio",
        label: "Bio",
        type: "textarea",
        required: true,
        helperText: "Write a short introduction (150–300 characters).",
        placeholder: "A concise summary of your journey and advising philosophy to students.",
        rows: 5,
        minLength: 150,
        maxLength: 300,
      }
    );

    // Your Experience Section
    fields.push(
      {
        name: "section_experience",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Catalyst for Innovation
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Define your functional mastery to be matched with the next big idea
            </Typography>
          </div>
        ),
      },
      {
        name: "years_of_experience",
        label: "Years of Experience",
        type: "number",
        required: true,
        placeholder: "Enter your years of experience",
      },
      {
        name: "industry_experience",
        label: "Industry Experience",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.industry_experience) ? formData.industry_experience : parseMultiSelectStr(formData.industry_experience);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("industry_experience", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${
                    error ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.industry_experience.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                      {item}
                      <button
                        type="button"
                        onClick={() => handleInputChange("industry_experience", arr.filter((x) => x !== item))}
                        className="hover:text-blue-900"
                        aria-label="Remove"
                      >
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "functional_experience",
        label: "Functional Experience",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.functional_experience) ? formData.functional_experience : parseMultiSelectStr(formData.functional_experience);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("functional_experience", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${
                    error ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.functional_experience.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 text-green-700 text-xs font-medium">
                      {item}
                      <button
                        type="button"
                        onClick={() => handleInputChange("functional_experience", arr.filter((x) => x !== item))}
                        className="hover:text-green-900"
                        aria-label="Remove"
                      >
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "technology_stack",
        label: "Technology Stack",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.technology_stack) ? formData.technology_stack : parseMultiSelectStr(formData.technology_stack);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("technology_stack", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${
                    error ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.technology_stack.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-medium">
                      {item}
                      <button
                        type="button"
                        onClick={() => handleInputChange("technology_stack", arr.filter((x) => x !== item))}
                        className="hover:text-purple-900"
                        aria-label="Remove"
                      >
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      }
    );

    // Your Acknowledgment Section (Mentor's Promise + capacity only for mentors; students see generic Acknowledgment)
    const isStudent = (user?.role || "").toLowerCase() === "student";
    fields.push(
      {
        name: "section_acknowledgment",
        type: "custom",
        component: (
          <div className="flex flex-col gap-1 py-4 border-t border-b border-slate-200 mt-4 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              {isStudent ? "Acknowledgment" : "The Mentor's Promise"}
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              {isStudent ? (
                <>
                  The Foundation of Trust
                  <br />
                  Built on global standards for security, privacy, and intellectual property protection.
                </>
              ) : (
                "Set your capacity to shape the future while upholding the highest standards of trust"
              )}
            </Typography>
          </div>
        ),
      },
      ...(isStudent ? [] : [{
        name: "maximum_engagements",
        label: "Maximum Engagements Assigned",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error) => (
          <div className="relative">
            <select
              value={formData.maximum_engagements || ""}
              onChange={(e) => handleInputChange("maximum_engagements", e.target.value)}
              className={`w-full rounded-lg border ${
                error ? "border-red-400" : "border-slate-200"
              } bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
            >
              <option value="">Select a maximum number of engagements to be assigned</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
          </div>
        ),
      }]),
      {
        name: "privacy_policy",
        label: "",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange) =>
          renderPrivacyAcknowledgementRow("privacy_policy_mentor_student", handleInputChange),
      }
    );

    return fields;
  };

  // Get changed fields only
  const getChangedFields = (currentData, initialData) => {
    const changed = {};
    
    // Common fields for both mentor and partner
    if (currentData.firstName !== initialData.firstName || currentData.lastName !== initialData.lastName) {
      if (currentData.firstName !== initialData.firstName) changed.firstName = currentData.firstName;
      if (currentData.lastName !== initialData.lastName) changed.lastName = currentData.lastName;
    }
    if (currentData.email !== initialData.email) changed.email = currentData.email;
    if (currentData.phone !== initialData.phone) changed.phone = currentData.phone;
    if (currentData.country !== initialData.country) changed.country = currentData.country;
    if (currentData.state !== initialData.state) changed.state = currentData.state;
    if (currentData.city !== initialData.city) changed.city = currentData.city;
    if (currentData.timezone !== initialData.timezone) changed.timezone = currentData.timezone;
    if (currentData.linkedin_profile !== initialData.linkedin_profile) changed.linkedin_profile = currentData.linkedin_profile;
    if (currentData.secondary_email !== initialData.secondary_email) changed.secondary_email = currentData.secondary_email;
    if (currentData.tag_line !== initialData.tag_line) changed.tag_line = currentData.tag_line;
    if (currentData.bio !== initialData.bio) changed.bio = currentData.bio;
    
    // Mentor-specific fields (industry/functional/tech are arrays, compare as comma-separated)
    if (user?.role === "mentor") {
      if (currentData.years_of_experience !== initialData.years_of_experience) changed.years_of_experience = currentData.years_of_experience;
      if (toCommaSeparated(currentData.industry_experience) !== toCommaSeparated(initialData.industry_experience)) changed.industry_experience = currentData.industry_experience;
      if (toCommaSeparated(currentData.functional_experience) !== toCommaSeparated(initialData.functional_experience)) changed.functional_experience = currentData.functional_experience;
      if (toCommaSeparated(currentData.technology_stack) !== toCommaSeparated(initialData.technology_stack)) changed.technology_stack = currentData.technology_stack;
      if (currentData.maximum_engagements !== initialData.maximum_engagements) changed.maximum_engagements = currentData.maximum_engagements;
    }
    
    // Corporate sponsor–specific fields
    if (user?.role === "corporate_sponsor") {
      if (currentData.sponsor_name !== initialData.sponsor_name) changed.sponsor_name = currentData.sponsor_name;
      if (currentData.legal_entity_name !== initialData.legal_entity_name) changed.legal_entity_name = currentData.legal_entity_name;
      if (toCommaSeparated(currentData.industry_sector) !== toCommaSeparated(initialData.industry_sector)) changed.industry_sector = currentData.industry_sector;
      if (currentData.website_url !== initialData.website_url) changed.website_url = currentData.website_url;
      if (currentData.sponsorship_program !== initialData.sponsorship_program) changed.sponsorship_program = currentData.sponsorship_program;
      if (currentData.contact_name !== initialData.contact_name) changed.contact_name = currentData.contact_name;
      if (currentData.contact_role !== initialData.contact_role) changed.contact_role = currentData.contact_role;
      if (currentData.contact_email !== initialData.contact_email) changed.contact_email = currentData.contact_email;
      if (currentData.contact_phone !== initialData.contact_phone) changed.contact_phone = currentData.contact_phone;
      if (currentData.secondary_contact_name !== initialData.secondary_contact_name) changed.secondary_contact_name = currentData.secondary_contact_name;
      if (currentData.secondary_contact_role !== initialData.secondary_contact_role) changed.secondary_contact_role = currentData.secondary_contact_role;
      if (currentData.secondary_contact_email !== initialData.secondary_contact_email) changed.secondary_contact_email = currentData.secondary_contact_email;
      if (currentData.secondary_contact_phone !== initialData.secondary_contact_phone) changed.secondary_contact_phone = currentData.secondary_contact_phone;
      if (currentData.mentorship_registration !== initialData.mentorship_registration) changed.mentorship_registration = currentData.mentorship_registration;
      if (currentData.pilot_access !== initialData.pilot_access) changed.pilot_access = currentData.pilot_access;
      if (currentData.sponsor_pitch_day !== initialData.sponsor_pitch_day) changed.sponsor_pitch_day = currentData.sponsor_pitch_day;
      if (currentData.reporting_frequency !== initialData.reporting_frequency) changed.reporting_frequency = currentData.reporting_frequency;
      if (currentData.public_visibility !== initialData.public_visibility) changed.public_visibility = currentData.public_visibility;
    }

    // Ecosystem partner–specific fields
    if (user?.role === "ecosystem_partner") {
      if (currentData.partner_name !== initialData.partner_name) changed.partner_name = currentData.partner_name;
      if (currentData.partner_type !== initialData.partner_type) changed.partner_type = currentData.partner_type;
      if (currentData.partner_category !== initialData.partner_category) changed.partner_category = currentData.partner_category;
      if (currentData.website_url !== initialData.website_url) changed.website_url = currentData.website_url;
      if (currentData.external_contact_name !== initialData.external_contact_name) changed.external_contact_name = currentData.external_contact_name;
      if (currentData.external_contact_role !== initialData.external_contact_role) changed.external_contact_role = currentData.external_contact_role;
      if (currentData.contact_email !== initialData.contact_email) changed.contact_email = currentData.contact_email;
      if (currentData.contact_phone !== initialData.contact_phone) changed.contact_phone = currentData.contact_phone;
      if (currentData.secondary_contact_name !== initialData.secondary_contact_name) changed.secondary_contact_name = currentData.secondary_contact_name;
      if (currentData.secondary_contact_role !== initialData.secondary_contact_role) changed.secondary_contact_role = currentData.secondary_contact_role;
      if (currentData.secondary_contact_email !== initialData.secondary_contact_email) changed.secondary_contact_email = currentData.secondary_contact_email;
      if (currentData.secondary_contact_phone !== initialData.secondary_contact_phone) changed.secondary_contact_phone = currentData.secondary_contact_phone;
      if (currentData.benefit_name !== initialData.benefit_name) changed.benefit_name = currentData.benefit_name;
      if (currentData.benefit_type !== initialData.benefit_type) changed.benefit_type = currentData.benefit_type;
      if (currentData.benefit_description !== initialData.benefit_description) changed.benefit_description = currentData.benefit_description;
      if (currentData.validity_period !== initialData.validity_period) changed.validity_period = currentData.validity_period;
      if (currentData.benefit_renewable !== initialData.benefit_renewable) changed.benefit_renewable = currentData.benefit_renewable;
      if (currentData.provisioning_process !== initialData.provisioning_process) changed.provisioning_process = currentData.provisioning_process;
      if (currentData.mentorship_registration !== initialData.mentorship_registration) changed.mentorship_registration = currentData.mentorship_registration;
      if (currentData.pilot_access !== initialData.pilot_access) changed.pilot_access = currentData.pilot_access;
      if (currentData.sponsor_pitch_day !== initialData.sponsor_pitch_day) changed.sponsor_pitch_day = currentData.sponsor_pitch_day;
      if (currentData.reporting_frequency !== initialData.reporting_frequency) changed.reporting_frequency = currentData.reporting_frequency;
      if (currentData.public_visibility !== initialData.public_visibility) changed.public_visibility = currentData.public_visibility;
    }
    
    // Student education and extra fields
    if (user?.role === "student") {
      if (currentData.collage !== initialData.collage) changed.collage = currentData.collage;
      if (currentData.year !== initialData.year) changed.year = currentData.year;
      if (currentData.primary_stream !== initialData.primary_stream) changed.primary_stream = currentData.primary_stream;
      if (currentData.specialization !== initialData.specialization) changed.specialization = currentData.specialization;
      if (currentData.github_url !== initialData.github_url) changed.github_url = currentData.github_url;
      if (currentData.innovation_excites_you !== initialData.innovation_excites_you) changed.innovation_excites_you = currentData.innovation_excites_you;
      if (currentData.success !== initialData.success) changed.success = currentData.success;
      if (currentData.achievement !== initialData.achievement) changed.achievement = currentData.achievement;
    }
    
    // Investor-specific fields
    if (user?.role === "investor") {
      if (currentData.investor_name !== initialData.investor_name) changed.investor_name = currentData.investor_name;
      if (currentData.investor_type !== initialData.investor_type) changed.investor_type = currentData.investor_type;
      if (currentData.fund_size_range !== initialData.fund_size_range) changed.fund_size_range = currentData.fund_size_range;
      if (currentData.website_url !== initialData.website_url) changed.website_url = currentData.website_url;
      if (currentData.x_profile !== initialData.x_profile) changed.x_profile = currentData.x_profile;
      if (currentData.contact_name !== initialData.contact_name) changed.contact_name = currentData.contact_name;
      if (currentData.contact_role !== initialData.contact_role) changed.contact_role = currentData.contact_role;
      if (currentData.contact_email !== initialData.contact_email) changed.contact_email = currentData.contact_email;
      if (currentData.contact_phone !== initialData.contact_phone) changed.contact_phone = currentData.contact_phone;
      if (currentData.secondary_contact_name !== initialData.secondary_contact_name) changed.secondary_contact_name = currentData.secondary_contact_name;
      if (currentData.secondary_contact_role !== initialData.secondary_contact_role) changed.secondary_contact_role = currentData.secondary_contact_role;
      if (currentData.secondary_contact_email !== initialData.secondary_contact_email) changed.secondary_contact_email = currentData.secondary_contact_email;
      if (currentData.secondary_contact_phone !== initialData.secondary_contact_phone) changed.secondary_contact_phone = currentData.secondary_contact_phone;
      if (currentData.stage_focus !== initialData.stage_focus) changed.stage_focus = currentData.stage_focus;
      if (currentData.domain_focus !== initialData.domain_focus) changed.domain_focus = currentData.domain_focus;
      if (currentData.investment_renewable !== initialData.investment_renewable) changed.investment_renewable = currentData.investment_renewable;
      if (currentData.minimum_traction !== initialData.minimum_traction) changed.minimum_traction = currentData.minimum_traction;
      if (currentData.mandatory_pitch_material !== initialData.mandatory_pitch_material) changed.mandatory_pitch_material = currentData.mandatory_pitch_material;
    }
    
    return changed;
  };

  const handleSave = async (formDataToSave, mode = "draft") => {
    // For submit mode (not admin_edit), require both Privacy Policy and Terms and Conditions to be read and acknowledged
    if (mode === "submit" && !isAdminEdit) {
      if (!hasViewedPrivacyPolicy || !hasViewedTermsConditions) {
        setSaveMessage({ type: "error", text: "Please read the Privacy Policy and Terms and Conditions first, then agree in each popup." });
        return;
      }
      if (!privacyPolicyAcknowledged || !termsConditionsAcknowledged) {
        setSaveMessage({ type: "error", text: "Please acknowledge the Privacy Policy and Terms and Conditions before submitting." });
        return;
      }
    }

    // A verified phone is required whenever someone finalises their OWN profile.
    // Partner-like roles and investors have a single "Save" that runs in "draft"
    // mode — that Save *is* their submission, so gating on mode === "submit" alone
    // would exempt them entirely. Mentor/student drafts stay saveable so progress is
    // never lost, and a number already on file is grandfathered.
    const isSelfFinalSave =
      !isAdminEdit &&
      mode !== "admin_edit" &&
      (mode === "submit" || isPartnerLikeRole(user?.role) || user?.role === "investor");

    if (isSelfFinalSave && !phoneVerified) {
      setSaveMessage({
        type: "error",
        text: 'Please verify your phone number — use "Verify with OTP" under the phone field.',
      });
      return;
    }

    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      let profilePictureUrl = user?.profile_picture_url || null;
      const hasPhotoChanged = uploadedFile !== null;

      if (uploadedFile) {
        try {
          const uploadResponse = await uploadUserImage(uploadedFile);
          let uploadData = uploadResponse.data;
          if (uploadData?.data?.data) {
            uploadData = uploadData.data;
          }
          const imageUrl = uploadData?.data?.url || uploadData?.url || uploadData?.data?.data?.url;
          
          if (uploadResponse.success && imageUrl) {
            profilePictureUrl = imageUrl;
          } else {
            setSaveMessage({ 
              type: "error", 
              text: uploadResponse.error || uploadData?.error || "Failed to upload image. Please try again." 
            });
            setIsSaving(false);
            return;
          }
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          setSaveMessage({ 
            type: "error", 
            text: uploadError.error || "Failed to upload image. Please try again." 
          });
          setIsSaving(false);
          return;
        }
      }

      const isPartnerOrInvestor = isPartnerLikeRole(user?.role) || user?.role === "investor";
      if (isPartnerOrInvestor) {
        const accountId = formDataToSave.accountId ?? formDataToSave.account_id ?? initialFormData.accountId ?? initialFormData.account_id ?? user?.account_id;
        const name = formDataToSave.sponsor_name ?? formDataToSave.partner_name ?? formDataToSave.investor_name ?? "";
        const fundingAmt = formDataToSave.sponsorship_program
          ? { bronze: "2500", silver: "10000", gold: "15000", platinum: "25000" }[formDataToSave.sponsorship_program]
          : formDataToSave.funding_amount != null ? String(formDataToSave.funding_amount) : null;
        const account = {
          id: accountId || undefined,
          name: name || null,
          legal_entity_name: (formDataToSave.legal_entity_name ?? formDataToSave.partner_type ?? formDataToSave.investor_type) || null,
          industry: (formDataToSave.industry_sector != null ? toCommaSeparated(formDataToSave.industry_sector) : formDataToSave.partner_category) || null,
          website: formDataToSave.website_url || null,
          funding_amount: fundingAmt ?? null,
          type: user?.role === "investor" ? (formDataToSave.investor_type || null) : user?.role === "ecosystem_partner" ? (formDataToSave.partner_type || null) : null,
          category: user?.role === "ecosystem_partner" ? (formDataToSave.partner_category != null && formDataToSave.partner_category !== "" ? (typeof formDataToSave.partner_category === "string" ? formDataToSave.partner_category : toCommaSeparated(formDataToSave.partner_category)) : null) : null,
          Category: user?.role === "ecosystem_partner" ? (formDataToSave.partner_category != null && formDataToSave.partner_category !== "" ? (typeof formDataToSave.partner_category === "string" ? formDataToSave.partner_category : toCommaSeparated(formDataToSave.partner_category)) : null) : null,
          fund_range: user?.role === "investor" ? (formDataToSave.fund_size_range || null) : null,
          company_logo: profilePictureUrl || null,
          tagline: formDataToSave.tag_line || null,
          linkedin: formDataToSave.linkedin_profile ? `https://linkedin.com/in/${String(formDataToSave.linkedin_profile).replace(/^\/+/, "")}` : null,
          x: formDataToSave.x_profile ? `https://x.com/${String(formDataToSave.x_profile).replace(/^\/+/, "").replace(/^@/, "")}` : null,
          stage_focus: formDataToSave.stage_focus || null,
          domain_focus: formDataToSave.domain_focus || null,
          traction_required: formDataToSave.minimum_traction || null,
          pitch_material: formDataToSave.mandatory_pitch_material || null,
          report_frequency: formDataToSave.reporting_frequency || null,
          funding_configuration: formDataToSave.sponsorship_program || null,
          SponsorshipProgram: toApiSponsorshipProgram(formDataToSave.sponsorship_program),
          profile_visibility: formDataToSave.public_visibility ?? null,
          benefit_name: formDataToSave.benefit_name || null,
          benefit_type: formDataToSave.benefit_type || null,
          benefit_description: formDataToSave.benefit_description || null,
          validity: formDataToSave.validity_period || null,
          provisioning_process: formDataToSave.provisioning_process || null,
          ProvisioningProcess: user?.role === "ecosystem_partner" ? (formDataToSave.provisioning_process || null) : null,
          SponsorPitchDay:
            user?.role === "ecosystem_partner" || user?.role === "corporate_sponsor"
              ? (formDataToSave.sponsor_pitch_day || null)
              : null,
          mentorship: formDataToSave.mentorship_registration ?? false,
          pilot_access: formDataToSave.pilot_access ?? false,
        };
        // Primary contact: contact_name/contact_email/contact_phone/contact_role (or external_* for ecosystem_partner)
        const primaryName = (formDataToSave.contact_name ?? formDataToSave.external_contact_name ?? "").trim();
        const primaryNameParts = primaryName ? primaryName.split(/\s+/).filter(Boolean) : [];
        const primaryFirstName = primaryNameParts[0] || (formDataToSave.firstName ?? user.first_name) || null;
        const primaryLastName = primaryNameParts.length > 1 ? primaryNameParts.slice(1).join(" ") : (formDataToSave.lastName ?? user.last_name) || null;
        const primaryContactId = formDataToSave.primary_contact_id || user.id;
        const contact = {
          id: String(primaryContactId),
          email: (formDataToSave.contact_email || formDataToSave.email || user.email || "").trim() || null,
          secondary_email: formDataToSave.secondary_email || null,
          username: (formDataToSave.primary_contact_username || user.username) ?? null,
          phone: buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone) || buildE164Phone(formDataToSave.phone_country_code, formDataToSave.phone) || user.phone || null,
          role: user.role ?? null,
          company_role: (formDataToSave.contact_role ?? formDataToSave.external_contact_role ?? "").trim() || null,
          is_active: user.is_active !== false,
          account_id: accountId ?? null,
          first_name: primaryFirstName,
          last_name: primaryLastName,
          profession: user.profession ?? null,
          organization: user.organization ?? null,
          experience_years: user.experience_years ?? null,
          years_of_experience: user.years_of_experience ?? null,
          industry: null,
          functional_experience: null,
          technology_stack: null,
          specialization: user.specialization ?? null,
          primary_stream: user.primary_stream ?? null,
          college: user.college ?? null,
          year: user.year ?? null,
          area_of_interest: user.area_of_interest ?? null,
          profile_picture_url: profilePictureUrl || null,
          country: formDataToSave.country || null,
          state: formDataToSave.state ?? null,
          city: formDataToSave.city ?? null,
          timezone: formDataToSave.timezone ?? null,
          bio: formDataToSave.bio ?? null,
          tagline: formDataToSave.tag_line ?? null,
          public_visibility: formDataToSave.public_visibility ?? false,
          // is_profile_completed: set true when user (investor/ecosystem_partner/corporate_sponsor) saves their own profile
          ...(isAdminEdit
            ? (user?.is_profile_completed !== undefined ? { is_profile_completed: !!user.is_profile_completed } : {})
            : { is_profile_completed: true }),
          skills: user.skills ?? null,
          expertise: user.expertise ?? null,
          interests: user.interests ?? null,
          linkedin_url: formDataToSave.linkedin_profile ? `https://linkedin.com/in/${String(formDataToSave.linkedin_profile).replace(/^\/+/, "")}` : null,
          x_url: formDataToSave.x_profile ? `https://x.com/${String(formDataToSave.x_profile).replace(/^\/+/, "").replace(/^@/, "")}` : null,
          github_url: user.github_url ?? null,
          facebook_url: user.facebook_url ?? null,
          calendly_link: user.calendly_link ?? null,
          preferred_commitment: user.preferred_commitment ?? null,
          mentor_status: user.mentor_status ?? null,
          maximum_engagements_assigned: user.maximum_engagements_assigned ?? null,
          activities_to_volunteer_for: user.activities_to_volunteer_for ?? null,
          availability_and_motivation: user.availability_and_motivation ?? null,
          innovation_excites_you: user.innovation_excites_you ?? null,
          success: user.success ?? null,
          achievement: user.achievement ?? null,
        };
        // Secondary contact (contact_2): from secondary_contact_* when we have secondary contact data
        const hasSecondaryContact = (formDataToSave.secondary_contact_name || formDataToSave.secondary_contact_email || "").trim().length > 0;
        let contact_2 = null;
        if (hasSecondaryContact) {
          const secName = (formDataToSave.secondary_contact_name || "").trim();
          const secNameParts = secName ? secName.split(/\s+/).filter(Boolean) : [];
          contact_2 = {
            ...(formDataToSave.secondary_contact_id ? { id: String(formDataToSave.secondary_contact_id) } : {}),
            email: (formDataToSave.secondary_contact_email || "").trim() || null,
            username: formDataToSave.secondary_contact_username ?? null,
            role: user.role ?? null,
            company_role: (formDataToSave.secondary_contact_role || "").trim() || null,
            first_name: secNameParts[0] || null,
            last_name: secNameParts.length > 1 ? secNameParts.slice(1).join(" ") : null,
            phone: formDataToSave.secondary_contact_phone
              ? buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone)
              : null,
          };
        }
        const payload = { account, contact, ...(contact_2 ? { contact_2 } : {}) };
        const response = await updateAccountContact(formDataToSave.primary_contact_id || user.id, payload);
        if (response.success) {
          const updatedFormData = {
            ...formDataToSave,
            photo: profilePictureUrl || photo,
            accountId: accountId || account.id,
            account_id: accountId || account.id,
          };
          setFormData(updatedFormData);
          setInitialFormData(updatedFormData);
          setPhoto(profilePictureUrl || photo);
          setUploadedFile(null);
          if (!isAdminEdit) {
            try {
              localStorage.setItem(`profile_${user?.id || user?.email}`, JSON.stringify({ ...updatedFormData }));
            } catch (e) {}
            // After successful save, mark profile completed so login/redirect goes to Home
            updateAuthUser({ ...authUser, is_profile_completed: true });
            // Redirect investor, corporate_sponsor, ecosystem_partner to dashboard after save
            setSaveMessage({ type: "success", text: "Changes saved successfully!" });
            setTimeout(() => {
              setSaveMessage({ type: "", text: "" });
              navigate("/");
            }, 500);
          } else {
            setSaveMessage({ type: "success", text: "Changes saved successfully!" });
            setTimeout(() => setSaveMessage({ type: "", text: "" }), 3000);
          }
        } else {
          setSaveMessage({ type: "error", text: response.error || "Failed to save. Please try again." });
        }
        setIsSaving(false);
        return;
      }

      let apiData = {};

      if (mode === "draft") {
        // For draft: only send changed fields
        const changedFields = getChangedFields(formDataToSave, initialFormData);
        
        if (Object.keys(changedFields).length === 0 && !hasPhotoChanged) {
          setSaveMessage({ type: "info", text: "No changes to save." });
          setIsSaving(false);
          return;
        }

        // Build API data from changed fields only (only include fields with actual values)
        if (changedFields.firstName !== undefined) apiData.first_name = changedFields.firstName || user.first_name || "";
        if (changedFields.lastName !== undefined) apiData.last_name = changedFields.lastName || user.last_name || "";
        if (changedFields.email !== undefined) apiData.email = changedFields.email || user.email || "";
        if (changedFields.phone !== undefined) apiData.phone = buildE164Phone(formDataToSave.phone_country_code, formDataToSave.phone) || user.phone || "";
        if (changedFields.country !== undefined) apiData.country = changedFields.country || user.country || "";
        
        // Only include optional fields if they have values
        if (changedFields.state !== undefined && changedFields.state) {
          apiData.state = changedFields.state;
        }
        if (changedFields.city !== undefined && changedFields.city) {
          apiData.city = changedFields.city;
        }
        if (changedFields.timezone !== undefined && changedFields.timezone) {
          apiData.timezone = changedFields.timezone;
        }
        
        if (changedFields.linkedin_profile !== undefined && changedFields.linkedin_profile) {
          apiData.linkedin_url = `https://linkedin.com/in/${changedFields.linkedin_profile.replace(/^\/+/, "")}`;
        }
        
        if (changedFields.secondary_email !== undefined && changedFields.secondary_email) {
          apiData.secondary_email = changedFields.secondary_email;
        }
        if (changedFields.tag_line !== undefined && changedFields.tag_line) {
          apiData.tagline = changedFields.tag_line;
        }
        if (changedFields.bio !== undefined && changedFields.bio) {
          apiData.bio = changedFields.bio;
        }
        
        if (changedFields.years_of_experience !== undefined && changedFields.years_of_experience) {
          const yearsExp = parseInt(changedFields.years_of_experience);
          if (!isNaN(yearsExp)) apiData.years_of_experience = yearsExp;
        }
        
        if (changedFields.industry_experience !== undefined && changedFields.industry_experience) {
          apiData.industry = toCommaSeparated(changedFields.industry_experience);
        }
        if (changedFields.functional_experience !== undefined && changedFields.functional_experience) {
          apiData.functional_experience = toCommaSeparated(changedFields.functional_experience);
        }
        if (changedFields.technology_stack !== undefined && changedFields.technology_stack) {
          apiData.technology_stack = toCommaSeparated(changedFields.technology_stack);
        }
        
        if (changedFields.maximum_engagements !== undefined && changedFields.maximum_engagements) {
          const maxEngagements = parseInt(changedFields.maximum_engagements);
          if (!isNaN(maxEngagements)) apiData.maximum_engagements_assigned = maxEngagements;
        }
        
        // Student education and extra fields
        if (user?.role === "student") {
          if (changedFields.collage !== undefined) apiData.collage = changedFields.collage || "";
          if (changedFields.year !== undefined) apiData.year = changedFields.year || "";
          if (changedFields.primary_stream !== undefined) apiData.primary_stream = changedFields.primary_stream || "";
          if (changedFields.specialization !== undefined) apiData.specialization = changedFields.specialization || "";
          if (changedFields.github_url !== undefined) {
            const gh = (changedFields.github_url || "").replace(/^\/+/, "").trim();
            apiData.github_url = gh ? `https://github.com/${gh}` : "";
          }
          if (changedFields.innovation_excites_you !== undefined) apiData.innovation_excites_you = changedFields.innovation_excites_you || "";
          if (changedFields.success !== undefined) apiData.success = changedFields.success || "";
          if (changedFields.achievement !== undefined) apiData.achievement = changedFields.achievement || "";
        }
        
        // Corporate sponsor: map form fields to API
        if (user?.role === "corporate_sponsor") {
          if (changedFields.sponsor_name !== undefined) apiData.sponsor_name = changedFields.sponsor_name || "";
          if (changedFields.legal_entity_name !== undefined) apiData.legal_entity_name = changedFields.legal_entity_name || "";
          if (changedFields.industry_sector !== undefined) apiData.industry_sector = toCommaSeparated(changedFields.industry_sector);
          if (changedFields.website_url !== undefined && changedFields.website_url) apiData.website_url = changedFields.website_url;
          if (changedFields.sponsorship_program !== undefined && changedFields.sponsorship_program) {
            const map = { bronze: 2500, silver: 10000, gold: 15000, platinum: 25000 };
            const amount = map[changedFields.sponsorship_program];
            if (amount != null) apiData.funding_amount = amount;
            apiData.funding_configuration = changedFields.sponsorship_program;
          }
          if (changedFields.contact_name !== undefined && changedFields.contact_name) apiData.contact_name = changedFields.contact_name;
          if (changedFields.contact_role !== undefined && changedFields.contact_role) apiData.contact_role = changedFields.contact_role;
          if (changedFields.contact_email !== undefined && changedFields.contact_email) apiData.contact_email = changedFields.contact_email;
          if (changedFields.contact_phone !== undefined && formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (changedFields.secondary_contact_name !== undefined && changedFields.secondary_contact_name) apiData.secondary_contact_name = changedFields.secondary_contact_name;
          if (changedFields.secondary_contact_role !== undefined && changedFields.secondary_contact_role) apiData.secondary_contact_role = changedFields.secondary_contact_role;
          if (changedFields.secondary_contact_email !== undefined && changedFields.secondary_contact_email) apiData.secondary_contact_email = changedFields.secondary_contact_email;
          if (changedFields.secondary_contact_phone !== undefined && formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (changedFields.mentorship_registration !== undefined) apiData.mentorship_registration = changedFields.mentorship_registration;
          if (changedFields.pilot_access !== undefined) apiData.pilot_access = changedFields.pilot_access;
          if (changedFields.sponsor_pitch_day !== undefined && changedFields.sponsor_pitch_day) apiData.sponsor_pitch_day = changedFields.sponsor_pitch_day;
          if (changedFields.reporting_frequency !== undefined && changedFields.reporting_frequency) apiData.reporting_frequency = changedFields.reporting_frequency;
          if (changedFields.public_visibility !== undefined) apiData.public_visibility = changedFields.public_visibility;
        }
        // Ecosystem partner: map form fields to API (partner_* -> sponsor_name/legal_entity_name/industry_sector, external_contact_* -> contact_*)
        if (user?.role === "ecosystem_partner") {
          if (changedFields.partner_name !== undefined) apiData.sponsor_name = changedFields.partner_name || "";
          if (changedFields.partner_type !== undefined) apiData.legal_entity_name = changedFields.partner_type || "";
          if (changedFields.partner_category !== undefined) apiData.industry_sector = typeof changedFields.partner_category === "string" ? changedFields.partner_category : toCommaSeparated(changedFields.partner_category);
          if (changedFields.website_url !== undefined && changedFields.website_url) apiData.website_url = changedFields.website_url;
          if (changedFields.external_contact_name !== undefined && changedFields.external_contact_name) apiData.contact_name = changedFields.external_contact_name;
          if (changedFields.external_contact_role !== undefined && changedFields.external_contact_role) apiData.contact_role = changedFields.external_contact_role;
          if (changedFields.contact_email !== undefined && changedFields.contact_email) apiData.contact_email = changedFields.contact_email;
          if (changedFields.contact_phone !== undefined && formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (changedFields.secondary_contact_name !== undefined && changedFields.secondary_contact_name) apiData.secondary_contact_name = changedFields.secondary_contact_name;
          if (changedFields.secondary_contact_role !== undefined && changedFields.secondary_contact_role) apiData.secondary_contact_role = changedFields.secondary_contact_role;
          if (changedFields.secondary_contact_email !== undefined && changedFields.secondary_contact_email) apiData.secondary_contact_email = changedFields.secondary_contact_email;
          if (changedFields.secondary_contact_phone !== undefined && formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (changedFields.benefit_name !== undefined && changedFields.benefit_name) apiData.benefit_name = changedFields.benefit_name;
          if (changedFields.benefit_type !== undefined && changedFields.benefit_type) apiData.benefit_type = changedFields.benefit_type;
          if (changedFields.benefit_description !== undefined && changedFields.benefit_description) apiData.benefit_description = changedFields.benefit_description;
          if (changedFields.validity_period !== undefined && changedFields.validity_period) apiData.validity_period = changedFields.validity_period;
          if (changedFields.benefit_renewable !== undefined) apiData.benefit_renewable = changedFields.benefit_renewable;
          if (changedFields.provisioning_process !== undefined && changedFields.provisioning_process) apiData.provisioning_process = changedFields.provisioning_process;
          if (changedFields.mentorship_registration !== undefined) apiData.mentorship_registration = changedFields.mentorship_registration;
          if (changedFields.pilot_access !== undefined) apiData.pilot_access = changedFields.pilot_access;
          if (changedFields.sponsor_pitch_day !== undefined && changedFields.sponsor_pitch_day) apiData.sponsor_pitch_day = changedFields.sponsor_pitch_day;
          if (changedFields.reporting_frequency !== undefined && changedFields.reporting_frequency) apiData.reporting_frequency = changedFields.reporting_frequency;
          if (changedFields.public_visibility !== undefined) apiData.public_visibility = changedFields.public_visibility;
        }
        
        // Investor-specific fields
        if (user?.role === "investor") {
          if (changedFields.investor_name !== undefined) apiData.sponsor_name = changedFields.investor_name || "";
          if (changedFields.investor_type !== undefined) apiData.legal_entity_name = changedFields.investor_type || "";
          if (changedFields.fund_size_range !== undefined) apiData.funding_configuration = changedFields.fund_size_range || "";
          if (changedFields.website_url !== undefined && changedFields.website_url) apiData.website_url = changedFields.website_url;
          if (changedFields.x_profile !== undefined && changedFields.x_profile) {
            apiData.x_url = `https://x.com/${changedFields.x_profile.replace(/^\/+/, "").replace(/^@/, "")}`;
          }
          if (changedFields.contact_name !== undefined && changedFields.contact_name) apiData.contact_name = changedFields.contact_name;
          if (changedFields.contact_role !== undefined && changedFields.contact_role) apiData.contact_role = changedFields.contact_role;
          if (changedFields.contact_email !== undefined && changedFields.contact_email) apiData.contact_email = changedFields.contact_email;
          if (changedFields.contact_phone !== undefined && formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (changedFields.secondary_contact_name !== undefined && changedFields.secondary_contact_name) apiData.secondary_contact_name = changedFields.secondary_contact_name;
          if (changedFields.secondary_contact_role !== undefined && changedFields.secondary_contact_role) apiData.secondary_contact_role = changedFields.secondary_contact_role;
          if (changedFields.secondary_contact_email !== undefined && changedFields.secondary_contact_email) apiData.secondary_contact_email = changedFields.secondary_contact_email;
          if (changedFields.secondary_contact_phone !== undefined && formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (changedFields.stage_focus !== undefined && changedFields.stage_focus) apiData.stage_focus = changedFields.stage_focus;
          if (changedFields.domain_focus !== undefined && changedFields.domain_focus) apiData.domain_focus = changedFields.domain_focus;
          if (changedFields.investment_renewable !== undefined) apiData.investment_renewable = changedFields.investment_renewable;
          if (changedFields.minimum_traction !== undefined && changedFields.minimum_traction) apiData.minimum_traction = changedFields.minimum_traction;
          if (changedFields.mandatory_pitch_material !== undefined && changedFields.mandatory_pitch_material) apiData.mandatory_pitch_material = changedFields.mandatory_pitch_material;
        }
      } else {
        // For submit: send all fields (only include fields with actual values)
        apiData = {
          first_name: formDataToSave.firstName || user.first_name || "",
          last_name: formDataToSave.lastName || user.last_name || "",
          email: formDataToSave.email || user.email || "",
          phone: buildE164Phone(formDataToSave.phone_country_code, formDataToSave.phone) || user.phone || "",
          country: formDataToSave.country || user.country || "",
        };

        // Only include optional fields if they have values
        if (formDataToSave.state) apiData.state = formDataToSave.state;
        if (formDataToSave.city) apiData.city = formDataToSave.city;
        if (formDataToSave.timezone) apiData.timezone = formDataToSave.timezone;
        
        if (formDataToSave.linkedin_profile) {
          apiData.linkedin_url = `https://linkedin.com/in/${formDataToSave.linkedin_profile.replace(/^\/+/, "")}`;
        }
        
        if (formDataToSave.secondary_email) apiData.secondary_email = formDataToSave.secondary_email;
        if (formDataToSave.tag_line) apiData.tagline = formDataToSave.tag_line;
        if (formDataToSave.bio) apiData.bio = formDataToSave.bio;
        
        if (formDataToSave.years_of_experience) {
          const yearsExp = parseInt(formDataToSave.years_of_experience);
          if (!isNaN(yearsExp)) apiData.years_of_experience = yearsExp;
        }
        
        if (formDataToSave.industry_experience) apiData.industry = toCommaSeparated(formDataToSave.industry_experience);
        if (formDataToSave.functional_experience) apiData.functional_experience = toCommaSeparated(formDataToSave.functional_experience);
        if (formDataToSave.technology_stack) apiData.technology_stack = toCommaSeparated(formDataToSave.technology_stack);
        
        if (formDataToSave.maximum_engagements) {
          const maxEngagements = parseInt(formDataToSave.maximum_engagements);
          if (!isNaN(maxEngagements)) apiData.maximum_engagements_assigned = maxEngagements;
        }
        
        if (user?.role === "student") {
          if (formDataToSave.collage) apiData.collage = formDataToSave.collage;
          if (formDataToSave.year) apiData.year = formDataToSave.year;
          if (formDataToSave.primary_stream) apiData.primary_stream = formDataToSave.primary_stream;
          if (formDataToSave.specialization) apiData.specialization = formDataToSave.specialization;
          const gh = (formDataToSave.github_url || "").replace(/^\/+/, "").trim();
          if (gh) apiData.github_url = `https://github.com/${gh}`;
          // Always send reflection fields with stable API keys (matches backend user model)
          apiData.innovation_excites_you = String(
            formDataToSave.innovation_excites_you ?? ""
          ).trim();
          apiData.success = String(formDataToSave.success ?? "").trim();
          apiData.achievement = String(formDataToSave.achievement ?? "").trim();
        }
        
        // Corporate sponsor: full submit
        if (user?.role === "corporate_sponsor") {
          if (formDataToSave.sponsor_name) apiData.sponsor_name = formDataToSave.sponsor_name;
          if (formDataToSave.legal_entity_name) apiData.legal_entity_name = formDataToSave.legal_entity_name;
          if (formDataToSave.industry_sector != null) apiData.industry_sector = toCommaSeparated(formDataToSave.industry_sector);
          if (formDataToSave.website_url) apiData.website_url = formDataToSave.website_url;
          if (formDataToSave.sponsorship_program) {
            const map = { bronze: 2500, silver: 10000, gold: 15000, platinum: 25000 };
            const amount = map[formDataToSave.sponsorship_program];
            if (amount != null) apiData.funding_amount = amount;
            apiData.funding_configuration = formDataToSave.sponsorship_program;
          }
          if (formDataToSave.contact_name) apiData.contact_name = formDataToSave.contact_name;
          if (formDataToSave.contact_role) apiData.contact_role = formDataToSave.contact_role;
          if (formDataToSave.contact_email) apiData.contact_email = formDataToSave.contact_email;
          if (formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (formDataToSave.secondary_contact_name) apiData.secondary_contact_name = formDataToSave.secondary_contact_name;
          if (formDataToSave.secondary_contact_role) apiData.secondary_contact_role = formDataToSave.secondary_contact_role;
          if (formDataToSave.secondary_contact_email) apiData.secondary_contact_email = formDataToSave.secondary_contact_email;
          if (formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (formDataToSave.mentorship_registration !== undefined) apiData.mentorship_registration = formDataToSave.mentorship_registration;
          if (formDataToSave.pilot_access !== undefined) apiData.pilot_access = formDataToSave.pilot_access;
          if (formDataToSave.sponsor_pitch_day) apiData.sponsor_pitch_day = formDataToSave.sponsor_pitch_day;
          if (formDataToSave.reporting_frequency) apiData.reporting_frequency = formDataToSave.reporting_frequency;
          if (formDataToSave.public_visibility !== undefined) apiData.public_visibility = formDataToSave.public_visibility;
        }
        // Ecosystem partner: full submit (map partner_* and external_contact_* to API)
        if (user?.role === "ecosystem_partner") {
          if (formDataToSave.partner_name) apiData.sponsor_name = formDataToSave.partner_name;
          if (formDataToSave.partner_type) apiData.legal_entity_name = formDataToSave.partner_type;
          if (formDataToSave.partner_category != null) apiData.industry_sector = typeof formDataToSave.partner_category === "string" ? formDataToSave.partner_category : toCommaSeparated(formDataToSave.partner_category);
          if (formDataToSave.website_url) apiData.website_url = formDataToSave.website_url;
          if (formDataToSave.external_contact_name) apiData.contact_name = formDataToSave.external_contact_name;
          if (formDataToSave.external_contact_role) apiData.contact_role = formDataToSave.external_contact_role;
          if (formDataToSave.contact_email) apiData.contact_email = formDataToSave.contact_email;
          if (formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (formDataToSave.secondary_contact_name) apiData.secondary_contact_name = formDataToSave.secondary_contact_name;
          if (formDataToSave.secondary_contact_role) apiData.secondary_contact_role = formDataToSave.secondary_contact_role;
          if (formDataToSave.secondary_contact_email) apiData.secondary_contact_email = formDataToSave.secondary_contact_email;
          if (formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (formDataToSave.benefit_name) apiData.benefit_name = formDataToSave.benefit_name;
          if (formDataToSave.benefit_type) apiData.benefit_type = formDataToSave.benefit_type;
          if (formDataToSave.benefit_description) apiData.benefit_description = formDataToSave.benefit_description;
          if (formDataToSave.validity_period) apiData.validity_period = formDataToSave.validity_period;
          if (formDataToSave.benefit_renewable !== undefined) apiData.benefit_renewable = formDataToSave.benefit_renewable;
          if (formDataToSave.provisioning_process) apiData.provisioning_process = formDataToSave.provisioning_process;
          if (formDataToSave.mentorship_registration !== undefined) apiData.mentorship_registration = formDataToSave.mentorship_registration;
          if (formDataToSave.pilot_access !== undefined) apiData.pilot_access = formDataToSave.pilot_access;
          if (formDataToSave.sponsor_pitch_day) apiData.sponsor_pitch_day = formDataToSave.sponsor_pitch_day;
          if (formDataToSave.reporting_frequency) apiData.reporting_frequency = formDataToSave.reporting_frequency;
          if (formDataToSave.public_visibility !== undefined) apiData.public_visibility = formDataToSave.public_visibility;
        }
        
        // Investor-specific fields
        if (user?.role === "investor") {
          if (formDataToSave.investor_name) apiData.sponsor_name = formDataToSave.investor_name;
          if (formDataToSave.investor_type) apiData.legal_entity_name = formDataToSave.investor_type;
          if (formDataToSave.fund_size_range) apiData.funding_configuration = formDataToSave.fund_size_range;
          if (formDataToSave.website_url) apiData.website_url = formDataToSave.website_url;
          if (formDataToSave.x_profile) {
            apiData.x_url = `https://x.com/${formDataToSave.x_profile.replace(/^\/+/, "").replace(/^@/, "")}`;
          }
          if (formDataToSave.contact_name) apiData.contact_name = formDataToSave.contact_name;
          if (formDataToSave.contact_role) apiData.contact_role = formDataToSave.contact_role;
          if (formDataToSave.contact_email) apiData.contact_email = formDataToSave.contact_email;
          if (formDataToSave.contact_phone) apiData.contact_phone = buildE164Phone(formDataToSave.contact_phone_country_code, formDataToSave.contact_phone);
          if (formDataToSave.secondary_contact_name) apiData.secondary_contact_name = formDataToSave.secondary_contact_name;
          if (formDataToSave.secondary_contact_role) apiData.secondary_contact_role = formDataToSave.secondary_contact_role;
          if (formDataToSave.secondary_contact_email) apiData.secondary_contact_email = formDataToSave.secondary_contact_email;
          if (formDataToSave.secondary_contact_phone) apiData.secondary_contact_phone = buildE164Phone(formDataToSave.secondary_contact_phone_country_code, formDataToSave.secondary_contact_phone);
          if (formDataToSave.stage_focus) apiData.stage_focus = formDataToSave.stage_focus;
          if (formDataToSave.domain_focus) apiData.domain_focus = formDataToSave.domain_focus;
          if (formDataToSave.investment_renewable !== undefined) apiData.investment_renewable = formDataToSave.investment_renewable;
          if (formDataToSave.minimum_traction) apiData.minimum_traction = formDataToSave.minimum_traction;
          if (formDataToSave.mandatory_pitch_material) apiData.mandatory_pitch_material = formDataToSave.mandatory_pitch_material;
        }
        
        // Only update mentor_status and is_profile_completed when self-submitting (not admin edit)
        if (!isAdminEdit) {
          if (user?.role === "mentor" && user?.mentor_status === "pending") {
            apiData.mentor_status = "submitted";
          }
          apiData.is_profile_completed = true;
        }
      }

      if (profilePictureUrl && (mode === "submit" || hasPhotoChanged)) {
        apiData.profile_picture_url = profilePictureUrl;
      }

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        // Get updated user data from response if available
        let responseData = response.data;
        if (responseData?.data?.data) {
          responseData = responseData.data;
        }
        const updatedUserFromAPI = responseData?.data?.user || responseData?.user || null;
        
        const updatedUserData = {
          ...user,
          ...apiData,
          profile_picture_url: profilePictureUrl,
          // Include updated user data from API response if available (including mentor_status)
          ...(updatedUserFromAPI ? {
            ...updatedUserFromAPI,
            profile_picture_url: updatedUserFromAPI.profile_picture_url || profilePictureUrl,
          } : {}),
        };
        
        if (!isAdminEdit) {
          updateAuthUser(updatedUserData);
        }
        const updatedFormData = {
          ...formDataToSave,
          photo: profilePictureUrl || photo,
        };
        setFormData(updatedFormData);
        setInitialFormData(updatedFormData); // Update initial data after save
        setPhoto(profilePictureUrl || photo);
        setUploadedFile(null);

        // Save to localStorage (only for self-edit, not admin edit)
        if (!isAdminEdit) {
          try {
            localStorage.setItem(`profile_${user?.id || user?.email}`, JSON.stringify({
              ...formDataToSave,
              photo: profilePictureUrl || photo,
            }));
          } catch (error) {
            console.error("Error saving profile data:", error);
          }
        }

        // If submitting for review, show appropriate completion screen (only for self-registration)
        if (mode === "submit" && !isAdminEdit) {
          if (isPartnerLikeRole(user?.role) || user?.role === "investor") {
            // For partners (incl. ecosystem_partner, corporate_sponsor) and investors, always show completion screen
            setCompletionType("pending");
            setShowCompletionScreen(true);
          } else if (user?.role === "mentor") {
            // For mentors, check mentor_status
            const finalMentorStatus = updatedUserFromAPI?.mentor_status || updatedUserData?.mentor_status || user?.mentor_status;
            
            if (finalMentorStatus === "approved") {
              // Show "Get Started" screen for approved mentors
              setCompletionType("approved");
              setShowCompletionScreen(true);
            } else if (finalMentorStatus === "pending" || finalMentorStatus === "submitted") {
              // Show completion screen for pending/submitted mentors
              setCompletionType("pending");
              setShowCompletionScreen(true);
            } else {
              // For other statuses, show success message
              setSaveMessage({ type: "success", text: "Registration submitted for review successfully!" });
              setTimeout(() => {
                setSaveMessage({ type: "", text: "" });
              }, 3000);
            }
          } else {
            // For other roles, show success message
            setSaveMessage({ type: "success", text: "Registration submitted for review successfully!" });
            setTimeout(() => {
              setSaveMessage({ type: "", text: "" });
            }, 3000);
          }
        } else {
          // For draft saves or admin edit, show success message
          setSaveMessage({
            type: "success",
            text: isAdminEdit ? "Changes saved successfully!" : "Draft saved successfully!",
          });
          setTimeout(() => {
            setSaveMessage({ type: "", text: "" });
          }, 3000);
        }
      } else {
        setSaveMessage({ 
          type: "error", 
          text: response.error || "Failed to complete registration. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error completing registration:", error);
      setSaveMessage({ 
        type: "error", 
        text: error.error || "An error occurred while completing your registration." 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getCurrentFormData = () => {
    // Use formData state which is now synced in real-time via onChange callback
    // Also get values from DOM as fallback for any fields that might not be synced
    const formElement = formRef.current?.querySelector('form');
    const currentData = { ...formData };
    
    if (formElement) {
      // Get all input values from DOM as fallback (for any fields that might not have name attributes)
      const inputs = formElement.querySelectorAll('input, select, textarea');
      const multiSelectFields = ["industry_experience", "functional_experience", "technology_stack"];
      inputs.forEach((input) => {
        if (input.name && input.type !== 'file') {
          if (multiSelectFields.includes(input.name)) {
            // Keep formData array for multi-selects; DOM .value is only first selected
            return;
          }
          // For radio groups: only use the checked radio's value (unchecked radios would overwrite with wrong value)
          if (input.type === 'radio') {
            if (input.checked) {
              currentData[input.name] = input.value;
            }
            return;
          }
          if (input.type === 'checkbox') {
            currentData[input.name] = input.checked;
          } else {
            const domValue = input.value;
            if (domValue !== undefined && domValue !== null) {
              currentData[input.name] = domValue;
            }
          }
        }
      });
    }

    // Post-process: ensure sponsorship_program is never "on" (use formData if DOM gave invalid value)
    const validSponsorshipTiers = ["bronze", "silver", "gold", "platinum"];
    if (currentData.sponsorship_program === "on" || !validSponsorshipTiers.includes((currentData.sponsorship_program || "").toLowerCase())) {
      currentData.sponsorship_program = validSponsorshipTiers.includes((formData.sponsorship_program || "").toLowerCase()) ? formData.sponsorship_program : "";
    }

    return currentData;
  };

  const handleSaveDraft = () => {
    const currentData = getCurrentFormData();
    handleSave(currentData, "draft");
  };

  const handleSubmitForReview = () => {
    const currentData = getCurrentFormData();
    handleSave(currentData, "submit");
  };

  const handleFormChange = useCallback((data) => {
    setFormData(data);
  }, []);
  const handleFormSubmit = useCallback((data) => {
    setFormData(data);
  }, []);

  // Mentor: disable "Submit for Review" until all mandatory fields are filled
  const isMentorSubmitDisabled = useMemo(() => {
    if (user?.role !== "mentor") return false;
    const first = (formData.firstName || "").trim();
    const last = (formData.lastName || "").trim();
    const email = (formData.email || "").trim();
    const phoneDigits = (formData.phone || "").replace(/\D/g, "");
    const hasPhoto = Boolean(user?.profile_picture_url || photo || uploadedFile);
    const country = (formData.country || "").trim();
    const state = (formData.state || "").trim();
    const timezone = (formData.timezone || "").trim();
    const secondaryEmail = (formData.secondary_email || "").trim();
    const bio = (formData.bio || "").trim();
    const bioLen = bio.length;
    const industry = parseMultiSelectStr(formData.industry_experience);
    const functional = parseMultiSelectStr(formData.functional_experience);
    const tech = parseMultiSelectStr(formData.technology_stack);
    const maxEng = formData.maximum_engagements;
    const yearsExp = formData.years_of_experience;

    return (
      !first ||
      !last ||
      !email ||
      phoneDigits.length < 10 ||
      !hasPhoto ||
      !country ||
      !state ||
      !timezone ||
      !secondaryEmail ||
      bioLen < 150 ||
      bioLen > 300 ||
      yearsExp === "" ||
      yearsExp == null ||
      industry.length === 0 ||
      functional.length === 0 ||
      tech.length === 0 ||
      maxEng === "" ||
      maxEng == null ||
      !privacyPolicyAcknowledged ||
      // Admins editing someone else's profile can't receive that person's SMS.
      (!isAdminEdit && !phoneVerified)
    );
  }, [
    isAdminEdit,
    phoneVerified,
    user?.role,
    user?.profile_picture_url,
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phone,
    formData.country,
    formData.state,
    formData.timezone,
    formData.secondary_email,
    formData.bio,
    formData.industry_experience,
    formData.functional_experience,
    formData.technology_stack,
    formData.maximum_engagements,
    formData.years_of_experience,
    photo,
    uploadedFile,
    privacyPolicyAcknowledged,
  ]);

  // When not in admin edit mode, wait for user (auth) to be loaded so role-specific form and data load correctly
  if (!isAdminEdit && !user) {
    return (
      <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8 items-center justify-center min-h-[320px]">
        <Typography as="p" className="text-tertiary-600">Loading...</Typography>
      </div>
    );
  }

  // Render completion screen if submission was successful (never for admin edit)
  if (showCompletionScreen && !isAdminEdit) {
    return (
      <CompletionScreen
        completionType={completionType}
        user={user}
      />
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
      <PrivacyPolicyModal
        open={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
        onAgree={() => {
          setHasViewedPrivacyPolicy(true);
          setShowPrivacyPolicy(false);
        }}
      />
      <TermsConditionsModal
        open={showTermsConditions}
        onClose={() => setShowTermsConditions(false)}
        onAgree={() => {
          setHasViewedTermsConditions(true);
          setShowTermsConditions(false);
        }}
      />

      {/* Header - hide when embedded in admin edit (parent provides header) */}
      {!isAdminEdit && (
        <div className="flex flex-col gap-2 w-full text-left">
          {isPartnerLikeRole(user?.role) || user?.role === "investor" ? (
            <>
              <Typography as="h1" className="text-2xl font-bold text-primary-900">
                {user?.role === "investor"
                  ? "Finalizing Your Investment Blueprint"
                  : "Finalizing Your Partnership Blueprint"}
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 max-w-3xl">
                {user?.role === "corporate_sponsor"
                  ? "Thank you for joining our platform. Please complete your profile."
                  : "Thank you for joining the IgniteNext ecosystem. Please complete your profile."}
              </Typography>
            </>
          ) : (
            <Typography as="h1" className="text-2xl font-bold text-primary-900">
              Complete Your Profile
            </Typography>
          )}
        </div>
      )}

      {/* Content */}
      <Card className="w-full">
        <div className="flex flex-col gap-6">
          {saveMessage.text && (
            <div
              className={`p-4 rounded-lg border ${
                saveMessage.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon
                  icon={
                    saveMessage.type === "success"
                      ? "lucide:check-circle"
                      : "lucide:alert-circle"
                  }
                  className={`h-5 w-5 ${
                    saveMessage.type === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                />
                <Typography as="p" className="text-sm font-medium">
                  {saveMessage.text}
                </Typography>
              </div>
            </div>
          )}

          <div ref={formRef}>
            <Form
              fields={getFormFields()}
              initialValues={formData}
              onChange={handleFormChange}
              onSubmit={handleFormSubmit}
              layout="horizontal-label"
              loading={isSaving}
              showActions={false}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200">
            {isAdminEdit || user?.role === "student" || isPartnerLikeRole(user?.role) || user?.role === "investor" ? (
              <Button
                type="button"
                variant="primary"
                size="medium"
                onClick={() => {
                  const currentData = getCurrentFormData();
                  handleSave(currentData, isAdminEdit ? "admin_edit" : user?.role === "student" ? "submit" : "draft");
                }}
                loading={isSaving}
              >
                Save
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="medium"
                  onClick={handleSaveDraft}
                  loading={isSaving}
                >
                  Save as Draft
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="medium"
                  onClick={handleSubmitForReview}
                  loading={isSaving}
                  disabled={isMentorSubmitDisabled}
                >
                  Submit for Review
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CompleteRegistration;

